import { useAyudaPanel } from '../hooks/useAyudaPanel.js';
import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { SETTINGS_TABS, type SettingsTabId } from '../lib/settingsTabs.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTabParam } from '../hooks/useTabParam.js';
import type { AccountModuleKey } from '@evo-hub/shared';
import { api, ApiError } from '../lib/api.js';
import { parseServerDate } from '../lib/date.js';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/ui/IconButton.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { KVRow } from '../components/ui/KVRow.js';
import { useUiFlag } from '../hooks/useUiPrefs.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { DataTable, type ColumnDef } from '../components/ui/DataTable.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { Pill } from '../components/PillGroup.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { Radio, RadioGroup } from '../components/ui/Radio.js';
import { Tabs } from '../components/ui/Tabs.js';
import { Toggle } from '../components/ui/Toggle.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { Badge } from '../components/ui/Badge.js';
import { SectionTitle } from '../components/ui/SectionTitle.js';
import { Field } from '../components/ui/Field.js';
import { ColorField } from '../components/ui/ColorField.js';
import { BackgroundField } from '../components/ui/BackgroundField.js';
import { StripeModeCard } from '../components/settings/StripeModeCard.js';
import { BRAND_ACCENT_DEFAULT, BRAND_TEXT_DEFAULT, BRAND_BG_DEFAULT, BRAND_PAGE_BG_DEFAULT } from '@evo-hub/shared';
import { Textarea } from '../components/ui/Textarea.js';
import { FileUploadZone } from '../components/ui/FileUploadZone.js';
import { Modal } from '../components/ui/Modal.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { SignaturePreview } from '../components/ui/SignaturePreview.js';
import { useToast } from '../components/ui/Toast.js';
import {
  useSavedReplies, useCreateSavedReply, useUpdateSavedReply,
  useDeleteSavedReply, useReorderSavedReplies, type SavedReply,
} from '../hooks/useSavedReplies.js';
import { PlantillasWhatsapp } from '../components/settings/PlantillasWhatsapp.js';
import { WhatsappConnect } from '../components/whatsapp/WhatsappConnect.js';
import {
  useBusinessContext, useSaveWebsiteUrl, useGenerateContext, useApproveContext,
  useAddFact, useUpdateFact, useDeleteFact, type GenerateResult,
} from '../hooks/useBusinessContext.js';
import { useMessagingAutonomy, useSetAutonomy, type AutonomyLevel } from '../hooks/useMessagingAutonomy.js';
import {
  useMessagingChannels, useChannelOptions, useRegisterChannel, useDisableChannel,
  type CanalCandidato, type CanalRegistrado,
} from '../hooks/useMessagingChannels.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { AutomationTab } from '../components/settings/AutomationTab.js';
import {
  useAllSettings,
  useUpdateSetting,
  useSecretStatus,
  useUpdateSecret,
  useOwnKeys,
  useUpdateOwnKey,
  useLlmSpend,
  useEmailAccounts,
  useAddEmailAccount,
  useTestEmailAccount,
  type ImapTestResult,
  useAccountSignature,
  useUpdateAccountSignature,
  useAuditLog,
  useLeadEngineSettings,
  useUpdateLeadEngineSettings,
  useLeadPipelines,
  useCreateLeadPipeline,
  useUpdateLeadPipeline,
  useDeleteLeadPipeline,
  useRunPipelineNow,
  useOpenClawStatus,
  useIssueOpenClawToken,
  type LeadPipeline,
  type LeadPipelineInput,
  type ScrapeZone,
} from '../hooks/useSettings.js';
import {
  useCompany, useUpdateCompany, useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount,
  useBillingItems, useCreateBillingItem, useUpdateBillingItem, useDeleteBillingItem,
} from '../hooks/useBilling.js';
import { PipelineEditorModal, SCRAPE_CATEGORY_OPTIONS } from '../components/leads/PipelineEditorModal.js';
import {
  useRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  type Rule,
  type CreateRuleInput,
} from '../hooks/useRules.js';
import {
  useJunkContacts, useDeleteJunkContacts,
  useRelationshipCategories, useCreateRelationshipCategory, useDeleteRelationshipCategory, useUpdateRelationshipCategory,
  useContactTagsList, useCreateContactTag, useDeleteContactTag,
  type JunkContactItem,
} from '../hooks/useContacts.js';
import { useFollowupRules, useDeleteFollowupRule, type FollowupRule } from '../hooks/useEmail.js';
import { useToolApprovals, useRevokeToolApproval } from '../hooks/useAssistant.js';
import { useUsers, useCreateUser, useUpdateUserRole, useSetUserActive, useApiUsage, useSetApiUsageLimit, useSubscription, useCreateSubscriptionCheckout, useCreateBillingPortal, useAvailablePlans, type SubscriptionSummary } from '../hooks/useSettings.js';
import { useSession } from '../contexts/SessionContext.js';
import { SettingsClaudePanel } from '../components/settings/SettingsClaudePanel.js';
import { TrialNotice } from '../components/subscription/TrialNotice.js';
import {
  useEmailCategoryDefs, useCreateEmailCategoryDef, useUpdateEmailCategoryDef, useDeleteEmailCategoryDef,
} from '../hooks/useEmailCategories.js';

// ── Section: General ──────────────────────────────────────────────────────────

/* La vieja pestaña General se partió: Preferencias (idioma) vive en Cuenta;
 * Identidad (logo/ícono) y Contacto público (alta/Calendly) en Marca. */

/**
 * Los dos interruptores del aviso de algo nuevo.
 *
 * Separados a propósito: hay momentos en que molesta el sonido y no la voz, y
 * al revés. Apagar los dos deja el aviso mudo, que también es una respuesta
 * válida.
 *
 * Viven en las preferencias del usuario en el servidor, así que apagarlos acá
 * los apaga en todas las máquinas.
 */
function AvisosCard() {
  const { t } = useTranslation();
  const [sonido, setSonido] = useUiFlag('aviso.sonido', true);
  const [voz, setVoz] = useUiFlag('aviso.voz', true);
  return (
    <div>
      <SectionTitle>{t('settings.avisos.title')}</SectionTitle>
      <KVGrid columns={1}>
        <KVRow
          label={t('settings.avisos.sonido')}
          value={<Toggle checked={sonido} onChange={setSonido} />}
        />
        <KVRow
          label={t('settings.avisos.voz')}
          value={<Toggle checked={voz} onChange={setVoz} />}
          last
        />
      </KVGrid>
    </div>
  );
}

function SessionCard() {
  const { t } = useTranslation();
  const { displayName, accountName, role } = useSession();
  return (
    <div>
      <SectionTitle>{t('settings.cards.session')}</SectionTitle>
      <KVGrid columns={1}>
        <KVRow label={t('settings.cards.sessionName')} value={displayName ?? '—'} />
        <KVRow label={t('settings.cards.sessionAccount')} value={accountName ?? '—'} />
        <KVRow
          label={t('settings.cards.sessionRole')}
          value={role === 'owner' ? t('settings.cards.roleOwner') : t('settings.cards.roleMember')}
          last
        />
      </KVGrid>
    </div>
  );
}

function PreferencesCard() {
  const { t, i18n } = useTranslation();
  const updateSetting = useUpdateSetting();

  function handleLangChange(lang: string) {
    void i18n.changeLanguage(lang);
    updateSetting.mutate({ key: 'ui_language', value: lang });
  }

  return (
    <div>
      <SectionTitle>{t('settings.cards.preferences')}</SectionTitle>
      <Field label={t('settings.generalSection.languageLabel')} hint={t('settings.generalSection.languageHint')}>
        <Dropdown<string>
          value={i18n.language.startsWith('en') ? 'en' : 'es'}
          options={[
            { value: 'es', label: t('settings.generalSection.langEs') },
            { value: 'en', label: t('settings.generalSection.langEn') },
          ]}
          onChange={handleLangChange}
          size="sm"
        />
      </Field>
    </div>
  );
}

function IdentityCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const updateSetting = useUpdateSetting();
  const { data: settings } = useAllSettings();
  const logoPath = settings?.['brand_logo_path'];
  const iconPath = settings?.['brand_icon_path'];
  // Vacío y ausente son lo mismo acá: sin acento propio. Guardar '' es como el
  // resto de los settings borra un valor (ver el onDelete del logo de arriba).
  const accent = settings?.['brand_accent'] || null;

  return (
    <div>
      <SectionTitle>{t('settings.cards.identity')}</SectionTitle>
      <Field label={t('settings.generalSection.brandLogo')} hint={t('settings.generalSection.brandLogoHint')}>
        <FileUploadZone
          uploadUrl="/api/settings/brand-asset/logo"
          value={logoPath ? [logoPath] : []}
          onUploaded={() => void qc.invalidateQueries({ queryKey: ['settings'] })}
          onDelete={() => updateSetting.mutate({ key: 'brand_logo_path', value: '' })}
          acceptMultiple={false}
          acceptedTypes="image/png,image/jpeg,image/webp,image/svg+xml"
        />
      </Field>
      <Field label={t('settings.generalSection.brandIcon')} hint={t('settings.generalSection.brandIconHint')}>
        <FileUploadZone
          uploadUrl="/api/settings/brand-asset/icon"
          value={iconPath ? [iconPath] : []}
          onUploaded={() => void qc.invalidateQueries({ queryKey: ['settings'] })}
          onDelete={() => updateSetting.mutate({ key: 'brand_icon_path', value: '' })}
          acceptMultiple={false}
          acceptedTypes="image/png,image/jpeg,image/webp,image/svg+xml"
        />
      </Field>
      {/* ── La marca, una sola para toda la cuenta (2026-09-01) ──────────
          La usan los formularios públicos, los correos de cobro, los
          documentos y el portal del cliente. Antes cada formulario tenía su
          propio panel de apariencia, y el resultado real fueron tres
          formularios de la misma cuenta con distinto color de texto y un logo
          que no era el de la cuenta. Ver server/services/brand.ts. */}
      <ColorField
        label={t('settings.generalSection.brandAccent')}
        hint={t('settings.generalSection.brandAccentHint')}
        value={accent ?? null}
        fallback={BRAND_ACCENT_DEFAULT}
        onCommit={hex => updateSetting.mutate({ key: 'brand_accent', value: hex })}
        onReset={() => updateSetting.mutate({ key: 'brand_accent', value: '' })}
      />
      <ColorField
        label={t('settings.generalSection.brandText')}
        hint={t('settings.generalSection.brandTextHint')}
        value={settings?.['brand_text'] || null}
        fallback={BRAND_TEXT_DEFAULT}
        onCommit={hex => updateSetting.mutate({ key: 'brand_text', value: hex })}
        onReset={() => updateSetting.mutate({ key: 'brand_text', value: '' })}
      />
      <BackgroundField
        label={t('settings.generalSection.brandBg')}
        hint={t('settings.generalSection.brandBgHint')}
        value={settings?.['brand_bg'] || null}
        fallback={BRAND_BG_DEFAULT}
        defaultLabel={t('settings.generalSection.brandBgDefault')}
        onChange={v => updateSetting.mutate({ key: 'brand_bg', value: v ?? '' })}
      />
      <BackgroundField
        label={t('settings.generalSection.brandPageBg')}
        hint={t('settings.generalSection.brandPageBgHint')}
        value={settings?.['brand_page_bg'] || null}
        fallback={BRAND_PAGE_BG_DEFAULT}
        defaultLabel={t('settings.generalSection.brandPageBgDefault')}
        onChange={v => updateSetting.mutate({ key: 'brand_page_bg', value: v ?? '' })}
      />
    </div>
  );
}

function PublicContactCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateSetting = useUpdateSetting();
  const { data: settings } = useAllSettings();
  const [calendlyVal, setCalendlyVal] = useState('');
  const [calendlySaved, setCalendlySaved] = useState(false);
  // A dónde llega el aviso cuando alguien llena un formulario. Vacío = a los
  // dueños de la cuenta, que es como funcionó siempre.
  const [avisoVal, setAvisoVal] = useState('');
  const [avisoSaved, setAvisoSaved] = useState(false);

  const currentCalendly = settings?.['calendly_url'] ?? '';
  useEffect(() => { setCalendlyVal(currentCalendly); }, [currentCalendly]);
  const currentAviso = settings?.['forms.notify_to'] ?? '';
  useEffect(() => { setAvisoVal(currentAviso); }, [currentAviso]);

  function saveAviso() {
    updateSetting.mutate(
      { key: 'forms.notify_to', value: avisoVal.trim() },
      { onSuccess: () => { setAvisoSaved(true); setTimeout(() => setAvisoSaved(false), 2000); } },
    );
  }

  function saveCalendly() {
    updateSetting.mutate(
      { key: 'calendly_url', value: calendlyVal.trim() },
      { onSuccess: () => { setCalendlySaved(true); setTimeout(() => setCalendlySaved(false), 2000); } },
    );
  }

  return (
    <div>
      <SectionTitle>{t('settings.cards.publicContact')}</SectionTitle>
      <Field label={t('settings.generalSection.onboardingLink')} hint={t('settings.generalSection.onboardingLinkHint')}>
        <Button size="sm" variant="outline" tone="cyan" onClick={() => navigate('/onboarding')}>
          {t('settings.generalSection.onboardingLinkBtn')}
        </Button>
      </Field>
      <Field label={t('settings.generalSection.calendlyUrl')} hint={t('settings.generalSection.calendlyUrlHint')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Input
            type="url"
            value={calendlyVal}
            onChange={v => { setCalendlyVal(v); setCalendlySaved(false); }}
            placeholder={t('settings.generalSection.calendlyUrlPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button
            size="sm"
            tone="cyan"
            variant={calendlySaved ? 'outline' : 'solid'}
            loading={updateSetting.isPending}
            onClick={saveCalendly}
          >
            {updateSetting.isPending ? t('settings.savingBtn') : calendlySaved ? t('settings.savedBtn') : t('settings.saveBtn')}
          </Button>
        </div>
      </Field>
      <Field label={t('settings.forms.notifyTo')} hint={t('settings.forms.notifyToHint')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Input
            value={avisoVal}
            onChange={v => { setAvisoVal(v); setAvisoSaved(false); }}
            placeholder={t('settings.forms.notifyToPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button
            size="sm"
            tone="cyan"
            variant={avisoSaved ? 'outline' : 'solid'}
            loading={updateSetting.isPending}
            onClick={saveAviso}
          >
            {updateSetting.isPending ? t('settings.savingBtn') : avisoSaved ? t('settings.savedBtn') : t('settings.saveBtn')}
          </Button>
        </div>
      </Field>
    </div>
  );
}

// ── Section: API & IA ────────────────────────────────────────────────────────

/**
 * Claves de PLATAFORMA. Solo se renderiza para is_platform_admin — una cuenta
 * cliente no debe siquiera saber que existen (condición explícita de Eduardo,
 * 2026-08-18). El backend además las cierra con requirePlatformAdmin: esto es
 * la capa de UI, el 403 es el gate real.
 */
/**
 * Claves PROPIAS del cliente ("bring your own"). Si carga la suya, se usa esa
 * en lugar de la de EvoHub y ese servicio NO se le factura en el consumo —
 * lo está pagando él directamente al proveedor.
 *
 * Nunca muestra un valor: el backend solo devuelve si está cargada o no. Una
 * clave que viaja de vuelta es una clave que puede terminar en un log.
 */
function OwnApiKeysSection() {
  const { t } = useTranslation();
  const { role } = useSession();
  const { data } = useOwnKeys(role === 'owner');
  const updateOwnKey = useUpdateOwnKey();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const toast = useToast();

  if (role !== 'owner') return null;

  function save(key: string) {
    const value = (drafts[key] ?? '').trim();
    if (!value) return;
    updateOwnKey.mutate({ key, value }, {
      onSuccess: () => {
        setDrafts(d => ({ ...d, [key]: '' }));
        toast.success(t('settings.ownKeys.saved', 'Clave guardada. Ese servicio deja de facturarse.'));
      },
    });
  }

  return (
    <div>
      <SectionTitle>{t('settings.ownKeys.title', 'Tus propias claves de API')}</SectionTitle>
      <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', marginTop: 0 }}>
        {t('settings.ownKeys.help', 'Si cargas tu propia clave, EvoHub la usa en lugar de la suya y ese servicio no te aparece en el consumo facturable — lo pagas tú directamente al proveedor.')}
      </p>
      {(data?.keys ?? []).map(k => (
        <KVRow
          key={k.key}
          label={k.key}
          value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
              <Pill size="xs" tone={k.configured ? 'success' : 'muted'}>
                {k.configured
                  ? t('settings.ownKeys.usingYours', 'usando la tuya')
                  : t('settings.ownKeys.usingPlatform', 'usando la de EvoHub')}
              </Pill>
              <Input
                value={drafts[k.key] ?? ''}
                onChange={v => setDrafts(d => ({ ...d, [k.key]: v }))}
                type="password"
                placeholder={t('settings.ownKeys.placeholder', 'Pega tu clave')}
              />
              <Button variant="outline" tone="cyan" size="sm"
                disabled={!(drafts[k.key] ?? '').trim()}
                loading={updateOwnKey.isPending}
                onClick={() => save(k.key)}>
                {t('common.save')}
              </Button>
            </span>
          }
        />
      ))}
    </div>
  );
}

/**
 * Claves de PLATAFORMA. Solo se renderiza para is_platform_admin — una cuenta
 * cliente no debe siquiera saber que existen (condición explícita de Eduardo,
 * 2026-08-18). El backend además las cierra con requirePlatformAdmin: esto es
 * la capa de UI, el 403 es el gate real.
 */
function ApiSection() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const { data: apiKeyStatus } = useSecretStatus('anthropic-api-key');
  const { data: groqKeyStatus } = useSecretStatus('groq-api-key');
  const { data: googleTtsKeyStatus } = useSecretStatus('google-tts-api-key');
  const { data: elevenLabsKeyStatus } = useSecretStatus('elevenlabs-api-key');
  const updateSecret = useUpdateSecret();
  const { data: spend } = useLlmSpend();

  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [apiKeySaved, setApiKeySaved] = useState(false);

  const [groqKey, setGroqKey] = useState('');
  const [showGroqKey, setShowGroqKey] = useState(false);
  const [groqKeySaved, setGroqKeySaved] = useState(false);

  const [googleTtsKey, setGoogleTtsKey] = useState('');
  const [showGoogleTtsKey, setShowGoogleTtsKey] = useState(false);
  const [googleTtsKeySaved, setGoogleTtsKeySaved] = useState(false);

  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [showElevenLabsKey, setShowElevenLabsKey] = useState(false);
  const [elevenLabsKeySaved, setElevenLabsKeySaved] = useState(false);

  const [voiceIdValue, setVoiceIdValue] = useState('');
  const [voiceIdSaved, setVoiceIdSaved] = useState(false);
  const currentVoiceId = settings?.['elevenlabs-voice-id'] ?? '';

  const [capValue, setCapValue] = useState('');
  const [capSaved, setCapSaved] = useState(false);
  const currentCap = settings?.['daily-cap-usd'] ?? '5.00';

  function saveApiKey() {
    if (!apiKey.trim()) return;
    updateSecret.mutate(
      { key: 'anthropic-api-key', value: apiKey.trim() },
      {
        onSuccess: () => {
          setApiKey('');
          setApiKeySaved(true);
          setTimeout(() => setApiKeySaved(false), 2000);
        },
      },
    );
  }

  function saveGroqKey() {
    if (!groqKey.trim()) return;
    updateSecret.mutate(
      { key: 'groq-api-key', value: groqKey.trim() },
      {
        onSuccess: () => {
          setGroqKey('');
          setGroqKeySaved(true);
          setTimeout(() => setGroqKeySaved(false), 2000);
        },
      },
    );
  }

  function saveGoogleTtsKey() {
    if (!googleTtsKey.trim()) return;
    updateSecret.mutate(
      { key: 'google-tts-api-key', value: googleTtsKey.trim() },
      {
        onSuccess: () => {
          setGoogleTtsKey('');
          setGoogleTtsKeySaved(true);
          setTimeout(() => setGoogleTtsKeySaved(false), 2000);
        },
      },
    );
  }

  function saveElevenLabsKey() {
    if (!elevenLabsKey.trim()) return;
    updateSecret.mutate(
      { key: 'elevenlabs-api-key', value: elevenLabsKey.trim() },
      {
        onSuccess: () => {
          setElevenLabsKey('');
          setElevenLabsKeySaved(true);
          setTimeout(() => setElevenLabsKeySaved(false), 2000);
        },
      },
    );
  }

  function saveVoiceId() {
    const val = voiceIdValue.trim() || currentVoiceId;
    if (!val) return;
    updateSetting.mutate(
      { key: 'elevenlabs-voice-id', value: val },
      { onSuccess: () => { setVoiceIdSaved(true); setTimeout(() => setVoiceIdSaved(false), 2000); } },
    );
  }

  function saveCap() {
    const val = parseFloat(capValue || currentCap);
    if (isNaN(val) || val < 0) return;
    updateSetting.mutate(
      { key: 'daily-cap-usd', value: val.toFixed(2) },
      { onSuccess: () => { setCapSaved(true); setTimeout(() => setCapSaved(false), 2000); } },
    );
  }

  const spendPct = spend ? Math.min(100, (spend.todaySpend / spend.cap) * 100) : 0;
  const spendColor = spendPct >= 90 ? '#ef4444' : spendPct >= 70 ? '#f59e0b' : '#00fac8';

  return (
    <div>
      <SectionTitle>{t('settings.ia.title')}</SectionTitle>

      {/* Anthropic API key */}
      <Field
        label={t('settings.ia.apiKeyLabel')}
        hint={apiKeyStatus?.configured ? t('settings.ia.apiKeyConfigured') : t('settings.ia.apiKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={v => { setApiKey(v); setApiKeySaved(false); }}
            placeholder={apiKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.apiKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowKey(s => !s)}>
            {showKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={apiKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveApiKey}>{updateSecret.isPending ? t('settings.savingBtn') : apiKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Groq API key — transcripción de voz (Fase 4) */}
      <Field
        label={t('settings.ia.groqKeyLabel')}
        hint={groqKeyStatus?.configured ? t('settings.ia.groqKeyConfigured') : t('settings.ia.groqKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showGroqKey ? 'text' : 'password'}
            value={groqKey}
            onChange={v => { setGroqKey(v); setGroqKeySaved(false); }}
            placeholder={groqKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.groqKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowGroqKey(s => !s)}>
            {showGroqKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={groqKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveGroqKey}>{updateSecret.isPending ? t('settings.savingBtn') : groqKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Google Cloud TTS key — voz de salida de Evo Brain (Fase 4.1) */}
      <Field
        label={t('settings.ia.googleTtsKeyLabel')}
        hint={googleTtsKeyStatus?.configured ? t('settings.ia.googleTtsKeyConfigured') : t('settings.ia.googleTtsKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showGoogleTtsKey ? 'text' : 'password'}
            value={googleTtsKey}
            onChange={v => { setGoogleTtsKey(v); setGoogleTtsKeySaved(false); }}
            placeholder={googleTtsKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.googleTtsKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowGoogleTtsKey(s => !s)}>
            {showGoogleTtsKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={googleTtsKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveGoogleTtsKey}>{updateSecret.isPending ? t('settings.savingBtn') : googleTtsKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* ElevenLabs key — voz de salida activa (Fase 4.1) */}
      <Field
        label={t('settings.ia.elevenLabsKeyLabel')}
        hint={elevenLabsKeyStatus?.configured ? t('settings.ia.elevenLabsKeyConfigured') : t('settings.ia.elevenLabsKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showElevenLabsKey ? 'text' : 'password'}
            value={elevenLabsKey}
            onChange={v => { setElevenLabsKey(v); setElevenLabsKeySaved(false); }}
            placeholder={elevenLabsKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.elevenLabsKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowElevenLabsKey(s => !s)}>
            {showElevenLabsKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={elevenLabsKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveElevenLabsKey}>{updateSecret.isPending ? t('settings.savingBtn') : elevenLabsKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* ElevenLabs voice_id — no es secreto, setting DB plano */}
      <Field label={t('settings.ia.elevenLabsVoiceLabel')} hint={t('settings.ia.elevenLabsVoiceHint')}>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={voiceIdValue || currentVoiceId}
            onChange={v => { setVoiceIdValue(v); setVoiceIdSaved(false); }}
            placeholder="CFhe2thhS6uqHsZesIpg"
            size="sm"
            fullWidth
          />
          <Button size="sm" tone="cyan" variant={voiceIdSaved ? 'outline' : 'solid'} loading={updateSetting.isPending} onClick={saveVoiceId}>{updateSetting.isPending ? t('settings.savingBtn') : voiceIdSaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Daily cap */}
      <Field label={t('settings.ia.dailyCapLabel')} hint={t('settings.ia.dailyCapHint')}>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>$</span>
          <div style={{ width: '96px', flexShrink: 0 }}>
            <Input
              type="number"
              min={0}
              step={0.5}
              value={capValue || currentCap}
              onChange={v => { setCapValue(v); setCapSaved(false); }}
              size="sm"
              fullWidth
            />
          </div>
          <Button size="sm" tone="cyan" variant={capSaved ? 'outline' : 'solid'} loading={updateSetting.isPending} onClick={saveCap}>{updateSetting.isPending ? t('settings.savingBtn') : capSaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Today's spend */}
      {spend && (
        <Field label={t('settings.ia.spendToday')} hint={`${new Date(spend.date).toLocaleDateString('es', { weekday: 'long', month: 'long', day: 'numeric' })}`}>
          <div className="space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--evo-bg-2)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${spendPct}%`, background: spendColor }}
                />
              </div>
              <span className="text-sm font-mono w-20 text-right" style={{ color: spendColor }}>
                ${spend.todaySpend.toFixed(4)} / ${spend.cap.toFixed(2)}
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {t('settings.ia.remaining')} ${spend.remaining.toFixed(4)}
            </p>
          </div>
        </Field>
      )}
    </div>
  );
}

// ── Section: Image Generation (IA) ───────────────────────────────────────────

function ImageGenerationSection() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const { data: openaiKeyStatus } = useSecretStatus('openai-api-key');
  const updateSecret = useUpdateSecret();

  const provider = settings?.['image_generation.provider'] ?? 'none';

  const [openaiKey, setOpenaiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);

  function saveOpenaiKey() {
    if (!openaiKey.trim()) return;
    updateSecret.mutate(
      { key: 'openai-api-key', value: openaiKey.trim() },
      {
        onSuccess: () => {
          setOpenaiKey('');
          setKeySaved(true);
          setTimeout(() => setKeySaved(false), 2000);
        },
      },
    );
  }

  return (
    <div style={{ marginTop: 'var(--evo-space-6)', paddingTop: 'var(--evo-space-6)', borderTop: '1px solid var(--evo-stroke)' }}>
      <SectionTitle>{t('settings.imageGen.title', 'Generación de imágenes IA')}</SectionTitle>

      {/* Provider */}
      <Field
        label={t('settings.imageGen.providerLabel', 'Proveedor')}
        hint={t('settings.imageGen.providerHint', 'Servicio de IA para generar imágenes de páginas web.')}
      >
        <RadioGroup
          value={provider}
          options={[
            { value: 'none', label: t('settings.imageGen.providerNone', 'Desactivado') },
            { value: 'openai', label: 'OpenAI' },
          ]}
          onChange={v => updateSetting.mutate({ key: 'image_generation.provider', value: v })}
        />
      </Field>

      {/* OpenAI config — only when openai selected */}
      {provider === 'openai' && (
        <>
          <Field
            label={t('settings.imageGen.openaiKeyLabel', 'OpenAI API Key')}
            hint={openaiKeyStatus?.configured
              ? t('settings.imageGen.openaiKeyConfigured', 'Clave configurada ✓')
              : t('settings.imageGen.openaiKeyMissing', 'Requerida para generar imágenes')}
          >
            <div className="flex items-center gap-2">
              <Input
                type={showKey ? 'text' : 'password'}
                value={openaiKey}
                onChange={v => { setOpenaiKey(v); setKeySaved(false); }}
                placeholder={openaiKeyStatus?.configured ? '••••••••' : 'sk-...'}
                size="sm"
                fullWidth
              />
              <Button variant="outline" tone="muted" size="sm" onClick={() => setShowKey(s => !s)}>
                {showKey ? t('settings.ia.hideKey', 'Ocultar') : t('settings.ia.showKey', 'Mostrar')}
              </Button>
              <Button size="sm" tone="cyan" variant={keySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveOpenaiKey}>{updateSecret.isPending ? t('settings.savingBtn') : keySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
            </div>
          </Field>

          <Field
            label={t('settings.imageGen.modelLabel', 'Modelo')}
            hint={t('settings.imageGen.modelHint', 'GPT Image 1 es el único modelo disponible.')}
          >
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
              GPT Image 1
            </span>
          </Field>
        </>
      )}
    </div>
  );
}

// ── Account row with inline signature editor ──────────────────────────────────

function AccountRow({ name }: { name: string }) {
  const { t } = useTranslation();
  const testAccount = useTestEmailAccount();
  const [testResult, setTestResult] = useState<ImapTestResult | null>(null);
  const [sigExpanded, setSigExpanded] = useState(false);
  const { data: sigData } = useAccountSignature(name);
  const updateSig = useUpdateAccountSignature();
  const [sigValue, setSigValue] = useState('');
  const [sigSaved, setSigSaved] = useState(false);

  useEffect(() => {
    if (sigData?.signature != null) setSigValue(sigData.signature);
  }, [sigData?.signature]);

  function handleTest() {
    setTestResult(null);
    testAccount.mutate(name, {
      onSuccess: r => setTestResult(r),
      // Un fallo de la petición (403, 500, red caída) también es un resultado
      // que hay que mostrar. Antes se veía igual que "todavía cargando".
      onError: (err: Error) => setTestResult({ ok: false, reason: 'unknown', detail: err.message }),
    });
  }

  function handleSaveSig() {
    updateSig.mutate({ name, signature: sigValue }, {
      onSuccess: () => { setSigSaved(true); setTimeout(() => setSigSaved(false), 2000); },
    });
  }

  return (
    <div className="rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="text-sm font-medium" style={{ color: 'var(--evo-fg-2)' }}>{name}</span>
        <div className="flex items-center gap-2">
          {testResult && (
            <span className="text-xs" style={{ color: testResult.ok ? 'var(--evo-cyan)' : 'var(--evo-danger)' }}>
              {testResult.ok
                ? t('settings.imap.connected')
                : (
                  <span className="flex items-center gap-1">
                    <Icon name="error" size={13} />
                    {/* El motivo real, no el mensaje crudo de la librería:
                        "Authentication failed" se entiende, "ETIMEDOUT" no. */}
                    {t(`settings.imap.testError.${testResult.reason ?? 'unknown'}`, testResult.detail ?? '')}
                  </span>
                )
              }
            </span>
          )}
          <Button variant="outline" tone="muted" size="sm" disabled={testAccount.isPending} onClick={handleTest}>
            {testAccount.isPending ? '…' : t('settings.imap.testBtn')}
          </Button>
          <Button
            variant="outline"
            tone={sigExpanded ? 'cyan' : 'muted'}
            size="sm"
            onClick={() => setSigExpanded(s => !s)}
          >
            {t('settings.accounts.signature')}
          </Button>
        </div>
      </div>

      {sigExpanded && (
        <div className="px-3 pb-3 pt-2" style={{ borderTop: '1px solid var(--evo-stroke)' }}>
          <div style={{ marginBottom: '8px' }}>
            <Textarea
              value={sigValue}
              onChange={v => { setSigValue(v); setSigSaved(false); }}
              placeholder={t('settings.accounts.signature_placeholder')}
              rows={8}
              fullWidth
              resize="vertical"
            />
          </div>
          {/* La previa vive acá abajo del campo, y renderiza HTML de verdad
              cuando la firma lo es. Antes era un div de texto plano sobre el
              fondo oscuro de la app: no se parecía a lo que llega al buzón ni
              cuando la firma era texto. */}
          {sigValue && (
            <div style={{ marginBottom: 'var(--evo-space-2)' }}>
              <SignaturePreview value={sigValue} />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={updateSig.isPending}
              onClick={handleSaveSig}
            >
              {updateSig.isPending ? t('settings.savingBtn') : t('settings.accounts.signature_save')}
            </Button>
            {sigSaved && <span className="text-xs" style={{ color: 'var(--evo-cyan)' }}>{t('settings.accounts.signature_saved')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: IMAP Accounts ────────────────────────────────────────────────────

const POLL_OPTIONS = [
  { value: '1' },
  { value: '5' },
  { value: '15' },
  { value: '30' },
  { value: '60' },
  { value: '0' },
];

const TRASH_OPTIONS = [
  { value: '7' },
  { value: '15' },
  { value: '30' },
  { value: '-1' },
];

// export: reusado tal cual (sin duplicar) por OnboardingPage.tsx — paso
// "conectar email" de la inducción (2026-08-05).
export function ImapSection() {
  const { t } = useTranslation();
  const { data: accounts = [], isLoading } = useEmailAccounts();
  const addAccount = useAddEmailAccount();
  const toast = useToast();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();

  const currentInterval = settings?.['imap-poll-interval'] ?? '5';

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', host: '', port: '993', user: '', pass: '' });

  const formFields = [
    { key: 'name', label: t('settings.imap.fields.name'), placeholder: t('settings.imap.placeholders.name'), type: 'text' },
    { key: 'host', label: t('settings.imap.fields.host'), placeholder: t('settings.imap.placeholders.host'), type: 'text' },
    { key: 'port', label: t('settings.imap.fields.port'), placeholder: t('settings.imap.placeholders.port'), type: 'number' },
    { key: 'user', label: t('settings.imap.fields.user'), placeholder: t('settings.imap.placeholders.user'), type: 'text' },
    { key: 'pass', label: t('settings.imap.fields.pass'), placeholder: t('settings.imap.placeholders.pass'), type: 'password' },
  ];

  function setField(key: string, val: string) {
    setForm(f => ({ ...f, [key]: val }));
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    addAccount.mutate(
      { name: form.name, host: form.host, port: form.port, user: form.user, pass: form.pass },
      {
        onSuccess: () => {
          setForm({ name: '', host: '', port: '993', user: '', pass: '' });
          setShowForm(false);
          toast.success(t('settings.emailAccountAdded', 'Casilla conectada.'));
        },
        // El error lo muestra el manejador global de mutaciones (main.tsx).
      },
    );
  }

  return (
    <div>
      <SectionTitle>{t('settings.imap.title')}</SectionTitle>

      <Field label={t('settings.imap.pollIntervalLabel')} hint={t('settings.imap.pollIntervalHint')}>
        <Dropdown<string>
          value={currentInterval}
          options={POLL_OPTIONS.map(o => ({ value: o.value, label: t(`settings.imap.poll.${o.value}`) }))}
          onChange={val => updateSetting.mutate({ key: 'imap-poll-interval', value: val })}
          size="sm"
        />
      </Field>

      <Field label={t('settings.imap.trashRetentionLabel')} hint={t('settings.imap.trashRetentionHint')}>
        <Dropdown<string>
          value={settings?.['trash-retention-days'] ?? '30'}
          options={TRASH_OPTIONS.map(o => ({ value: o.value, label: t(`settings.imap.trashOptions.${o.value}`) }))}
          onChange={val => updateSetting.mutate({ key: 'trash-retention-days', value: val })}
          size="sm"
        />
      </Field>

      {isLoading && (
        <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>
      )}

      {accounts.length === 0 && !isLoading && (
        <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
          {t('settings.imap.noAccounts')}
        </p>
      )}

      <div className="space-y-2 mb-4">
        {accounts.map(name => (
          <AccountRow key={name} name={name} />
        ))}
      </div>

      {/* Desde qué casilla salen los correos que manda Evo Hub (cotizaciones,
          facturas, proyectos, outreach). Vive acá, al lado de las casillas, y
          no en el bloque del Lead Engine: Villa Sión tiene ese módulo apagado,
          así que el selector no existía en su pantalla y mandar una cotización
          fallaba pidiéndoles configurar algo que no podían ver.
          Con una sola casilla el servidor la usa sola (mail/fromAccount.ts);
          esto es para elegir cuando hay varias. */}
      {accounts.length > 1 && (
        <Field label={t('settings.imap.fromAccountLabel')} hint={t('settings.imap.fromAccountHint')}>
          <Dropdown<string>
            value={settings?.['lead_engine.from_account'] || null}
            placeholder={t('settings.imap.fromAccountPlaceholder')}
            options={accounts.map(name => ({ value: name, label: name }))}
            onChange={val => updateSetting.mutate({ key: 'lead_engine.from_account', value: val })}
            size="sm"
          />
        </Field>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3 p-4 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.imap.formTitle')}</p>
          {formFields.map(f => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {f.label}
              </label>
              <Input
                type={f.type as 'text' | 'number' | 'password'}
                value={form[f.key as keyof typeof form]}
                onChange={v => setField(f.key, v)}
                placeholder={f.placeholder}
                required={f.key !== 'port'}
                size="sm"
                fullWidth
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={addAccount.isPending}>
              {addAccount.isPending ? t('settings.imap.savingAccount') : t('settings.imap.addAccountBtn')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setShowForm(false)}>
              {t('settings.imap.cancelBtn')}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowForm(true)}>
          {t('settings.imap.addAccount')}
        </Button>
      )}
    </div>
  );
}

// ── Section: Rules ────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateRuleInput = {
  matchType: 'sender',
  sender: '',
  domain: '',
  subjectPattern: '',
  originalCategory: '',
  newCategory: '',
};

function RulesSection() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [form, setForm] = useState<CreateRuleInput>(EMPTY_FORM);

  const { data, isLoading } = useRules(page, search);
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  function matchLabel(mt: string): string {
    const k = mt === 'sender+subject' ? 'senderSubject' : mt === 'domain+subject' ? 'domainSubject' : mt;
    return t(`settings.rules.matchTypes.${k}`);
  }

  function setField<K extends keyof CreateRuleInput>(key: K, val: CreateRuleInput[K]) {
    setForm(f => ({ ...f, [key]: val }));
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function openNew() {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(rule: Rule) {
    setEditingRule(rule);
    setForm({
      matchType: rule.matchType,
      sender: rule.sender,
      domain: rule.domain,
      ...(rule.subjectPattern !== null ? { subjectPattern: rule.subjectPattern } : {}),
      ...(rule.originalCategory !== null ? { originalCategory: rule.originalCategory } : {}),
      newCategory: rule.newCategory,
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingRule(null);
    setForm(EMPTY_FORM);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.newCategory.trim()) return;

    const payload: CreateRuleInput = {
      matchType: form.matchType,
      newCategory: form.newCategory,
      ...(form.sender ? { sender: form.sender } : {}),
      ...(form.domain ? { domain: form.domain } : {}),
      ...(form.subjectPattern ? { subjectPattern: form.subjectPattern } : {}),
      ...(form.originalCategory ? { originalCategory: form.originalCategory } : {}),
    };

    if (editingRule) {
      updateRule.mutate(
        { id: editingRule.id, ...payload },
        { onSuccess: () => handleCancel() },
      );
    } else {
      createRule.mutate(payload, { onSuccess: () => handleCancel() });
    }
  }

  async function handleDelete(rule: Rule) {
    const ok = await confirmAction({
      title: t('settings.rules.deleteConfirm', { pattern: rule.sender || rule.domain }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteRule.mutate(rule.id);
  }

  const isPending = createRule.isPending || updateRule.isPending;

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.rules.title')}</SectionTitle>

      {/* Search + New button */}
      <div className="flex items-center gap-2 mb-4">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
          <Input
            type="text"
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('settings.rules.searchPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button type="submit" variant="outline" tone="muted" size="sm">
            {t('settings.rules.searchBtn')}
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              tone="muted"
              size="sm"
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
            >
              <Icon name="close" size={14} />
            </Button>
          )}
        </form>
        {!showForm && (
          <Button variant="solid" tone="cyan" size="sm" onClick={openNew}>
            {t('settings.rules.newRule')}
          </Button>
        )}
      </div>

      {/* Inline form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="space-y-3 p-4 rounded mb-4"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
        >
          <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)' }}>
            {editingRule ? t('settings.rules.editTitle') : t('settings.rules.createTitle')}
          </p>

          {/* matchType */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.matchTypeLabel')}
            </label>
            <Dropdown<string>
              value={form.matchType}
              options={[
                { value: 'sender', label: t('settings.rules.matchTypes.sender') },
                { value: 'domain', label: t('settings.rules.matchTypes.domain') },
                { value: 'sender+subject', label: t('settings.rules.matchTypes.senderSubject') },
                { value: 'domain+subject', label: t('settings.rules.matchTypes.domainSubject') },
              ]}
              onChange={val => setField('matchType', val as CreateRuleInput['matchType'])}
              size="sm"
            />
          </div>

          {/* sender */}
          {(form.matchType === 'sender' || form.matchType === 'sender+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldSender')}
              </label>
              <Input
                type="text"
                value={form.sender ?? ''}
                onChange={v => setField('sender', v)}
                placeholder={t('settings.rules.placeholderSender')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* domain */}
          {(form.matchType === 'domain' || form.matchType === 'domain+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldDomain')}
              </label>
              <Input
                type="text"
                value={form.domain ?? ''}
                onChange={v => setField('domain', v)}
                placeholder={t('settings.rules.placeholderDomain')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* subjectPattern */}
          {(form.matchType === 'sender+subject' || form.matchType === 'domain+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldSubjectPattern')}
              </label>
              <Input
                type="text"
                value={form.subjectPattern ?? ''}
                onChange={v => setField('subjectPattern', v)}
                placeholder={t('settings.rules.placeholderSubject')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* originalCategory */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.fieldOrigCategory')}
            </label>
            <Input
              type="text"
              value={form.originalCategory ?? ''}
              onChange={v => setField('originalCategory', v)}
              placeholder={t('settings.rules.placeholderOptional')}
              size="sm"
              fullWidth
            />
          </div>

          {/* newCategory */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.fieldNewCategory')}
            </label>
            <Input
              type="text"
              value={form.newCategory}
              onChange={v => setField('newCategory', v)}
              placeholder={t('settings.rules.placeholderCategory')}
              required
              size="sm"
              fullWidth
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={isPending}>
              {isPending ? t('settings.rules.savingRule') : editingRule ? t('settings.rules.updateRule') : t('settings.rules.createRule')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={handleCancel}>
              {t('settings.rules.cancelBtn')}
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
              {[
                t('settings.rules.headers.type'),
                t('settings.rules.headers.pattern'),
                t('settings.rules.headers.subject'),
                t('settings.rules.headers.origCat'),
                t('settings.rules.headers.newCat'),
                t('settings.rules.headers.applications'),
                t('settings.rules.headers.actions'),
              ].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</td></tr>
            )}
            {!isLoading && (data?.rows ?? []).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.rules.noRules')}</td></tr>
            )}
            {(data?.rows ?? []).map(rule => (
              <tr
                key={rule.id}
                onClick={() => openEdit(rule)}
                style={{ borderBottom: '1px solid var(--evo-stroke)', cursor: 'pointer' }}
              >
                <td className="px-3 py-2 font-medium">{matchLabel(rule.matchType)}</td>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--evo-fg-3)' }}>
                  {rule.sender || rule.domain || '—'}
                </td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{rule.subjectPattern ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{rule.originalCategory ?? '—'}</td>
                <td className="px-3 py-2 font-medium" style={{ color: 'var(--evo-cyan)' }}>{rule.newCategory}</td>
                <td className="px-3 py-2 text-right" style={{ color: 'var(--evo-fg-3)' }}>{rule.appliedCount}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      tone="muted"
                      size="xs"
                      onClick={e => { e.stopPropagation(); openEdit(rule); }}
                    >
                      {t('settings.rules.editBtn')}
                    </Button>
                    <Button
                      variant="outline"
                      tone="danger"
                      size="xs"
                      disabled={deleteRule.isPending}
                      onClick={e => { e.stopPropagation(); void handleDelete(rule); }}
                    >
                      {t('settings.rules.deleteBtn')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs" style={{ color: 'var(--evo-fg-3)' }}>
          <span>{t('settings.rules.paginationText', { count: data.total, page: data.page, pages: data.pages })}</span>
          <div className="flex gap-1">
            <Button variant="ghost" tone="muted" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
            <Button variant="ghost" tone="muted" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>›</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Audit Log ────────────────────────────────────────────────────────

function fmtDate(s: string): string {
  return parseServerDate(s).toLocaleString('es', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function AuditSection() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useAuditLog(page, 50);

  return (
    <div>
      <SectionTitle>{t('settings.audit.title')}</SectionTitle>
      <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
              {[
                t('settings.audit.headers.action'),
                t('settings.audit.headers.entity'),
                t('settings.audit.headers.id'),
                t('settings.audit.headers.date'),
              ].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</td></tr>
            )}
            {isError && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: '#ef4444' }}>{t('settings.audit.errorLoading')}</td></tr>
            )}
            {!isLoading && !isError && (data?.rows ?? []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.audit.noRecords')}</td></tr>
            )}
            {(data?.rows ?? []).map(row => (
              <tr
                key={row.id}
                style={{ borderBottom: '1px solid var(--evo-stroke)' }}
              >
                <td className="px-3 py-2 font-medium">{row.action}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{row.entityType ?? '—'}</td>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--evo-fg-3)' }}>{row.entityId ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(row.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs" style={{ color: 'var(--evo-fg-3)' }}>
          <span>{t('settings.audit.paginationText', { count: data.total, page: data.page, pages: data.pages })}</span>
          <div className="flex gap-1">
            <Button variant="ghost" tone="muted" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
            <Button variant="ghost" tone="muted" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>›</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Cleanup ──────────────────────────────────────────────────────────

function CleanupSection() {
  const { t } = useTranslation();
  const { data: junk, isLoading } = useJunkContacts();
  const deleteJunk = useDeleteJunkContacts();
  const [showList, setShowList] = useState(false);
  const [selected, setSelected] = useState<JunkContactItem[]>([]);
  const [confirmAll, setConfirmAll] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);

  const total = junk?.total ?? 0;
  const items = junk?.items ?? [];

  function handleDeleteSelected() {
    deleteJunk.mutate(selected.map(c => c.id), {
      onSuccess: res => { setDone(res.deleted); setSkippedCount(res.skipped.length); setSelected([]); },
    });
  }

  function handleDeleteAll() {
    deleteJunk.mutate(undefined, {
      onSuccess: res => { setDone(res.deleted); setSkippedCount(res.skipped.length); setShowList(false); setConfirmAll(false); },
    });
  }

  const columns: ColumnDef<JunkContactItem>[] = [
    {
      id: 'name',
      header: t('settings.cleanup.junkContacts.columnName'),
      accessorFn: row => [row.firstName, row.lastName].filter(Boolean).join(' ') || '—',
    },
    {
      id: 'business',
      header: t('settings.cleanup.junkContacts.columnBusiness'),
      accessorFn: row => row.businessName || '—',
    },
    {
      id: 'email',
      header: t('settings.cleanup.junkContacts.columnEmail'),
      cell: ({ row }) => (
        <span style={{ color: row.original.primaryEmail ? 'var(--evo-fg-2)' : 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
          {row.original.primaryEmail || '—'}
        </span>
      ),
    },
    {
      id: 'phone',
      header: t('settings.cleanup.junkContacts.columnPhone'),
      cell: ({ row }) => (
        <span style={{ color: row.original.primaryPhone ? 'var(--evo-fg-2)' : 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
          {row.original.primaryPhone || '—'}
        </span>
      ),
    },
    {
      id: 'source',
      header: t('settings.cleanup.junkContacts.columnSource'),
      accessorKey: 'source',
    },
    {
      id: 'reason',
      header: t('settings.cleanup.junkContacts.columnReason'),
      enableSorting: false,
      cell: ({ row }) => (
        <Pill tone="amber" variant="subtle" size="sm">
          {t(row.original.reason === 'no_data'
            ? 'settings.cleanup.junkContacts.reasonNoData'
            : 'settings.cleanup.junkContacts.reasonBothInvalid')}
        </Pill>
      ),
    },
  ];

  return (
    <DetailSection title={t('settings.cleanup.junkContacts.title')}>
      <p style={{ margin: '0 0 var(--evo-space-3)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>
        {t('settings.cleanup.junkContacts.description')}
      </p>

      {isLoading ? (
        <LoadingState size="sm" />
      ) : total === 0 && done === null ? (
        <EmptyState title={t('settings.cleanup.junkContacts.empty')} size="sm" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          {/* Counter + action row */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
            <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-amber)' }}>
              {t('settings.cleanup.junkContacts.count', { count: total })}
            </span>

            {total > 0 && (
              <>
                <Button variant="outline" tone="neutral" size="sm" onClick={() => setShowList(l => !l)}>
                  {showList ? t('settings.cleanup.junkContacts.hideList') : t('settings.cleanup.junkContacts.viewList')}
                </Button>

                {!confirmAll && done === null && (
                  <Button variant="outline" tone="danger" size="sm" onClick={() => setConfirmAll(true)}>
                    {t('settings.cleanup.junkContacts.deleteAll')}
                  </Button>
                )}

                {confirmAll && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
                      {t('settings.cleanup.junkContacts.confirmDeleteAll', { count: total })}
                    </span>
                    <Button variant="solid" tone="danger" size="sm" loading={deleteJunk.isPending} onClick={handleDeleteAll}>
                      {t('common.delete')}
                    </Button>
                    <Button variant="ghost" tone="muted" size="sm" onClick={() => setConfirmAll(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {done !== null && (
            <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', color: 'var(--evo-cyan)' }}>
              {t('settings.cleanup.deleted', { count: done })}
              {skippedCount > 0 && (
                <span style={{ color: 'var(--evo-amber)' }}> — {t('settings.cleanup.skipped', { count: skippedCount })}</span>
              )}
            </span>
          )}

          {/* Preview table */}
          {showList && total > 0 && (
            <div>
              <SelectionActionBar
                visible={selected.length > 0}
                countLabel={String(selected.length)}
                onClearSelection={() => setSelected([])}
              >
                <SelectionActionBar.Action
                  label={t('settings.cleanup.junkContacts.deleteSelected', { count: selected.length })}
                  tone="danger"
                  confirmRequired
                  loading={deleteJunk.isPending}
                  onClick={handleDeleteSelected}
                />
              </SelectionActionBar>
              <DataTable<JunkContactItem>
                key={total}
                columns={columns}
                data={items}
                size="sm"
                density="compact"
                selectable
                onSelectionChange={setSelected}
                getRowId={row => String(row.id)}
                emptyMessage={t('settings.cleanup.junkContacts.empty')}
              />
            </div>
          )}
        </div>
      )}
    </DetailSection>
  );
}



// ── Section: Ayuda ────────────────────────────────────────────────────────────

function AyudaCard() {
  const { t } = useTranslation();
  const ayuda = useAyudaPanel();
  return (
    <div>
      <SectionTitle>{t('ayuda.titulo')}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {t('ayuda.desdeAjustes')}
        </p>
        <div>
          <Button variant="outline" tone="cyan" size="sm" onClick={ayuda.abrir}>
            {t('ayuda.abrir')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Section: Categories & Tags ────────────────────────────────────────────────

function CategoriesTagsSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: relCategories = [], isLoading: relLoading } = useRelationshipCategories();
  const createRelCat = useCreateRelationshipCategory();
  const deleteRelCat = useDeleteRelationshipCategory();
  const updateRelCat = useUpdateRelationshipCategory();
  const { data: tags = [], isLoading: tagsLoading } = useContactTagsList();
  const createTag = useCreateContactTag();
  const deleteTag = useDeleteContactTag();

  const { data: emailCategories = [], isLoading: emailCategoriesLoading } = useEmailCategoryDefs();
  const createEmailCat = useCreateEmailCategoryDef();
  const updateEmailCat = useUpdateEmailCategoryDef();
  const deleteEmailCat = useDeleteEmailCategoryDef();
  const [editingEmailCatId, setEditingEmailCatId] = useState<number | null>(null);
  const [editEmailCatName, setEditEmailCatName] = useState('');
  const [editEmailCatColor, setEditEmailCatColor] = useState('#888888');
  const [showNewEmailCat, setShowNewEmailCat] = useState(false);
  const [newEmailCat, setNewEmailCat] = useState({ name: '', color: '#888888' });

  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editLabelEs, setEditLabelEs] = useState('');
  const [editLabelEn, setEditLabelEn] = useState('');
  const [editColor, setEditColor] = useState('#888888');

  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState({ slug: '', labelEs: '', color: '#888888' });
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTag, setNewTag] = useState({ slug: '', label: '', color: '#888888' });
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  function handleCreateEmailCat(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmailCat.name.trim()) return;
    createEmailCat.mutate(newEmailCat, {
      onSuccess: () => { setNewEmailCat({ name: '', color: '#888888' }); setShowNewEmailCat(false); },
    });
  }

  async function handleDeleteEmailCat(id: number) {
    const ok = await confirmAction({ title: t('settings.categoriesAndTags.delete_confirm', { count: 0 }), tone: 'danger' });
    if (!ok) return;
    deleteEmailCat.mutate(id, {
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  function handleCreateCat(e: React.FormEvent) {
    e.preventDefault();
    if (!newCat.slug || !newCat.labelEs) return;
    createRelCat.mutate(
      { slug: newCat.slug, labelEs: newCat.labelEs, labelEn: newCat.labelEs, color: newCat.color },
      { onSuccess: () => { setNewCat({ slug: '', labelEs: '', color: '#888888' }); setShowNewCat(false); } },
    );
  }

  async function handleDeleteCat(slug: string, isSystem: boolean | number, count: number) {
    if (isSystem) { alert(t('settings.categoriesAndTags.cannot_delete_system')); return; }
    const ok = await confirmAction({
      title: t('settings.categoriesAndTags.delete_confirm', { count }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteRelCat.mutate(slug);
  }

  function handleCreateTag(e: React.FormEvent) {
    e.preventDefault();
    if (!newTag.slug || !newTag.label) return;
    createTag.mutate(
      { slug: newTag.slug, label: newTag.label, color: newTag.color },
      { onSuccess: () => { setNewTag({ slug: '', label: '', color: '#888888' }); setShowNewTag(false); } },
    );
  }

  async function handleDeleteTag(id: number, count: number) {
    const ok = await confirmAction({
      title: t('settings.categoriesAndTags.delete_confirm', { count }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteTag.mutate(id);
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.categoriesAndTags.title')}</SectionTitle>

      {/* Relationship categories */}
      <p className="text-sm font-medium mb-3" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.rel_title')}
      </p>

      {relLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {relCategories.map(cat => (
          <div
            key={cat.slug}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            {editingSlug === cat.slug ? (
              /* ── inline edit mode ── */
              <div className="flex items-center gap-2 flex-1">
                <div style={{ flexShrink: 0 }}>
                  <Input type="color" value={editColor} onChange={v => setEditColor(v)} size="sm" />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <Input value={editLabelEs} onChange={v => setEditLabelEs(v)} placeholder={t('settings.categoriesAndTags.label_es_label')} size="sm" fullWidth />
                  <Input value={editLabelEn} onChange={v => setEditLabelEn(v)} placeholder={t('settings.categoriesAndTags.label_en_label')} size="sm" fullWidth />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="solid"
                    tone="cyan"
                    size="sm"
                    disabled={updateRelCat.isPending}
                    onClick={() => {
                      if (!editLabelEs.trim() || !editLabelEn.trim()) {
                        alert(t('settings.categoriesAndTags.rename_error_empty'));
                        return;
                      }
                      updateRelCat.mutate(
                        { slug: cat.slug, patch: { labelEs: editLabelEs, labelEn: editLabelEn, color: editColor } },
                        { onSuccess: () => setEditingSlug(null) },
                      );
                    }}
                  >
                    {t('settings.categoriesAndTags.save_btn')}
                  </Button>
                  <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setEditingSlug(null)}>
                    {t('settings.categoriesAndTags.cancel_btn')}
                  </Button>
                </div>
              </div>
            ) : (
              /* ── display mode ── */
              <>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full shrink-0"
                    style={{ background: cat.color }}
                  />
                  <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{cat.labelEs}</span>
                  <span className="text-xs font-mono" style={{ color: 'var(--evo-fg-3)' }}>{cat.slug}</span>
                  {cat.isSystem ? (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--evo-cyan)22', color: 'var(--evo-cyan)', border: '1px solid var(--evo-cyan)44' }}>
                      {t('settings.categoriesAndTags.system_badge')}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.categoriesAndTags.contacts_using', { count: cat.count })}
                  </span>
                  {!cat.isSystem && (
                    <>
                      <Button variant="outline" tone="cyan" size="sm" onClick={() => {
                          setEditingSlug(cat.slug);
                          setEditLabelEs(cat.labelEs);
                          setEditLabelEn(cat.labelEn);
                          setEditColor(cat.color);
                        }}>
                        {t('settings.categoriesAndTags.edit_btn')}
                      </Button>
                      <Button variant="ghost" tone="danger" size="sm" disabled={deleteRelCat.isPending} onClick={() => { void handleDeleteCat(cat.slug, cat.isSystem, cat.count); }}>
                        {t('settings.categoriesAndTags.delete_btn')}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {showNewCat ? (
        <form onSubmit={handleCreateCat} className="flex items-end gap-2 p-3 rounded mb-6"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.slug_label')}</label>
            <Input value={newCat.slug} onChange={v => setNewCat(f => ({ ...f, slug: v.replace(/\s+/g, '_').toLowerCase() }))} placeholder={t('settings.categoriesAndTags.slug_placeholder')} required size="sm" fullWidth />
          </div>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.label_label')}</label>
            <Input value={newCat.labelEs} onChange={v => setNewCat(f => ({ ...f, labelEs: v }))} placeholder={t('settings.categoriesAndTags.label_placeholder')} required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newCat.color} onChange={color => setNewCat(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createRelCat.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewCat(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <div style={{ marginBottom: '24px' }}>
          <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewCat(true)}>
            {t('settings.categoriesAndTags.new_category_btn')}
          </Button>
        </div>
      )}

      {/* Tags */}
      <p className="text-sm font-medium mb-3" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.tags_title')}
      </p>

      {tagsLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {tags.length === 0 && !tagsLoading && (
          <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>
            {t('contacts.tag.none')}
          </p>
        )}
        {tags.map(tag => (
          <div
            key={tag.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: tag.color ?? '#888888' }} />
              <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{tag.label}</span>
              <span className="text-xs font-mono" style={{ color: 'var(--evo-fg-3)' }}>{tag.slug}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('settings.categoriesAndTags.contacts_using', { count: tag.count ?? 0 })}
              </span>
              <Button variant="ghost" tone="danger" size="sm" disabled={deleteTag.isPending} onClick={() => { void handleDeleteTag(tag.id, tag.count ?? 0); }}>
                {t('settings.categoriesAndTags.delete_btn')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {showNewTag ? (
        <form onSubmit={handleCreateTag} className="flex items-end gap-2 p-3 rounded"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.slug_label')}</label>
            <Input value={newTag.slug} onChange={v => setNewTag(f => ({ ...f, slug: v.replace(/\s+/g, '_').toLowerCase() }))} placeholder="ej. vip" required size="sm" fullWidth />
          </div>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.label_label')}</label>
            <Input value={newTag.label} onChange={v => setNewTag(f => ({ ...f, label: v }))} placeholder="VIP" required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newTag.color} onChange={color => setNewTag(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createTag.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewTag(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewTag(true)}>
          {t('settings.categoriesAndTags.new_tag_btn')}
        </Button>
      )}

      {/* Email categories — antes CATEGORY_COLORS hardcodeado en EmailPage.tsx */}
      <p className="text-sm font-medium mb-3 mt-8" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.email_categories_title')}
      </p>

      {emailCategoriesLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {emailCategories.map(cat => (
          <div
            key={cat.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            {editingEmailCatId === cat.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input type="color" value={editEmailCatColor} onChange={v => setEditEmailCatColor(v)} size="sm" />
                <Input value={editEmailCatName} onChange={v => setEditEmailCatName(v)} size="sm" fullWidth />
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="solid" tone="cyan" size="sm"
                    disabled={updateEmailCat.isPending}
                    onClick={() => {
                      if (!editEmailCatName.trim()) return;
                      updateEmailCat.mutate(
                        { id: cat.id, patch: { name: editEmailCatName, color: editEmailCatColor } },
                        {
                          onSuccess: () => setEditingEmailCatId(null),
                          onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
                        },
                      );
                    }}
                  >
                    {t('settings.categoriesAndTags.save_btn')}
                  </Button>
                  <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setEditingEmailCatId(null)}>
                    {t('settings.categoriesAndTags.cancel_btn')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: cat.color }} />
                  <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{cat.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" tone="cyan" size="sm" onClick={() => {
                    setEditingEmailCatId(cat.id);
                    setEditEmailCatName(cat.name);
                    setEditEmailCatColor(cat.color);
                  }}>
                    {t('settings.categoriesAndTags.edit_btn')}
                  </Button>
                  <Button variant="ghost" tone="danger" size="sm" disabled={deleteEmailCat.isPending} onClick={() => { void handleDeleteEmailCat(cat.id); }}>
                    {t('settings.categoriesAndTags.delete_btn')}
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {showNewEmailCat ? (
        <form onSubmit={handleCreateEmailCat} className="flex items-end gap-2 p-3 rounded"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.name_label')}</label>
            <Input value={newEmailCat.name} onChange={v => setNewEmailCat(f => ({ ...f, name: v }))} required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newEmailCat.color} onChange={color => setNewEmailCat(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createEmailCat.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewEmailCat(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewEmailCat(true)}>
          {t('settings.categoriesAndTags.new_category_btn')}
        </Button>
      )}
    </div>
  );
}



// ── Scraping Telemetry Card ───────────────────────────────────────────────────

interface TelemetrySummary {
  total_attempts: number;
  success_rate: number;
  by_status: Record<string, number>;
  last_failure: { timestamp: string; lead_id: string | null; error_message: string | null } | null;
}

// ── Respuestas guardadas (Mensajería Etapa 2, 2026-08-20) ────────────────────
// Administración: crear, editar, borrar, ordenar. La inserción con un clic
// vive en el composer de la bandeja, no acá.
function SavedRepliesSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useSavedReplies();
  const replies = data?.replies ?? [];
  const create = useCreateSavedReply();
  const update = useUpdateSavedReply();
  const remove = useDeleteSavedReply();
  const reorder = useReorderSavedReplies();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  // null = cerrado | 'new' = alta | number = edición de esa fila.
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState({ title: '', body: '' });

  function abrirNueva() {
    setForm({ title: '', body: '' });
    setEditing('new');
  }

  function abrirEdicion(r: SavedReply) {
    setForm({ title: r.title, body: r.body });
    setEditing(r.id);
  }

  function guardar() {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || !body) return;
    const onError = (err: unknown) => toast.error(err instanceof Error ? err.message : String(err));
    if (editing === 'new') {
      create.mutate({ title, body }, { onSuccess: () => setEditing(null), onError });
    } else if (typeof editing === 'number') {
      update.mutate({ id: editing, title, body }, { onSuccess: () => setEditing(null), onError });
    }
  }

  async function borrar(r: SavedReply) {
    const ok = await confirmAction({ title: t('settings.savedReplies.deleteConfirm', { title: r.title }), tone: 'danger' });
    if (!ok) return;
    remove.mutate(r.id, { onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)) });
  }

  // El servidor exige el orden COMPLETO, así que un movimiento de una posición
  // se traduce acá a la lista entera ya intercambiada.
  function mover(index: number, delta: number) {
    const destino = index + delta;
    if (destino < 0 || destino >= replies.length) return;
    const ids = replies.map(r => r.id);
    [ids[index], ids[destino]] = [ids[destino]!, ids[index]!];
    reorder.mutate(ids, { onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)) });
  }

  const guardando = create.isPending || update.isPending;

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.savedReplies.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.savedReplies.help')}
      </p>

      {isLoading ? (
        <LoadingState size="sm" />
      ) : replies.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('settings.savedReplies.emptyTitle')}
          description={t('settings.savedReplies.emptyHint')}
          action={<Button variant="solid" tone="cyan" size="sm" onClick={abrirNueva}>{t('settings.savedReplies.new')}</Button>}
        />
      ) : (
        <>
          <div className="space-y-1.5 mb-3">
            {replies.map((r, i) => (
              <div
                key={r.id}
                className="flex items-start justify-between gap-3 px-3 py-2 rounded"
                style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium" style={{ color: 'var(--evo-fg-1)' }}>{r.title}</span>
                  {/* Una línea: el texto completo se ve al editar. Un cuerpo de
                      diez renglones convertiría la lista en un muro. */}
                  <span
                    className="text-xs"
                    style={{
                      color: 'var(--evo-fg-3)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {r.body}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Sin ícono de flecha en el set: se rota el chevron, que ya
                      existe, en vez de sumar un asset nuevo por dos botones. */}
                  <IconButton
                    icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="chevron-down" size={14} /></span>}
                    ariaLabel={t('settings.savedReplies.moveUp')}
                    tone="muted" variant="ghost" size="md"
                    disabled={i === 0 || reorder.isPending}
                    onClick={() => mover(i, -1)}
                  />
                  <IconButton
                    icon={<Icon name="chevron-down" size={14} />}
                    ariaLabel={t('settings.savedReplies.moveDown')}
                    tone="muted" variant="ghost" size="md"
                    disabled={i === replies.length - 1 || reorder.isPending}
                    onClick={() => mover(i, 1)}
                  />
                  <IconButton
                    icon={<Icon name="pencil" size={14} />}
                    ariaLabel={t('common.edit')}
                    tone="muted" variant="outline" size="md"
                    onClick={() => abrirEdicion(r)}
                  />
                  <IconButton
                    icon={<Icon name="close" size={14} />}
                    ariaLabel={t('common.delete')}
                    tone="danger" variant="outline" size="md"
                    onClick={() => { void borrar(r); }}
                  />
                </div>
              </div>
            ))}
          </div>
          <Button variant="solid" tone="cyan" size="sm" onClick={abrirNueva}>
            {t('settings.savedReplies.new')}
          </Button>
        </>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('settings.savedReplies.new') : t('settings.savedReplies.edit')}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" tone="muted" size="sm" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid" tone="cyan" size="sm"
              loading={guardando}
              disabled={!form.title.trim() || !form.body.trim()}
              onClick={guardar}
            >
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            value={form.title}
            onChange={v => setForm(f => ({ ...f, title: v }))}
            label={t('settings.savedReplies.titleLabel')}
            placeholder={t('settings.savedReplies.titlePlaceholder')}
            maxLength={80}
            fullWidth
            autoFocus
          />
          <Textarea
            value={form.body}
            onChange={v => setForm(f => ({ ...f, body: v }))}
            label={t('settings.savedReplies.bodyLabel')}
            placeholder={t('settings.savedReplies.bodyPlaceholder')}
            rows={6}
            maxLength={2000}
            fullWidth
          />
        </div>
      </Modal>
    </div>
  );
}

// ── Canales conectados (2026-08-25) ──────────────────────────────────────────
// La pantalla que faltaba. Autorizar Facebook en Composio no registra el canal,
// y el poll solo mira los registrados — así que un cliente podía ver "conectado"
// y no recibir un solo mensaje durante una semana. Acá se ve la diferencia.
function MessagingChannelsSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useMessagingChannels();
  const canales = data?.channels ?? [];
  const opciones = useChannelOptions();
  const registrar = useRegisterChannel();
  const desactivar = useDisableChannel();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const [eligiendo, setEligiendo] = useState<CanalCandidato[] | null>(null);
  const errorDe = (err: unknown) => toast.error(err instanceof Error ? err.message : String(err));

  function abrirElección() {
    opciones.mutate('facebook', {
      onSuccess: r => setEligiendo(r.candidatos),
      onError: errorDe,
    });
  }

  async function quitar(c: CanalRegistrado) {
    const ok = await confirmAction({ title: t('settings.channels.disableConfirm', { label: c.label }), tone: 'danger' });
    if (!ok) return;
    desactivar.mutate(c.id, { onError: errorDe });
  }

  return (
    <div className="mb-8">
      {confirmDialog}
      <SectionTitle>{t('settings.channels.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.channels.help')}
      </p>

      {isLoading ? (
        <LoadingState size="sm" />
      ) : canales.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('settings.channels.emptyTitle')}
          description={t('settings.channels.emptyHint')}
        />
      ) : (
        <div className="space-y-1.5 mb-3">
          {canales.map(c => (
            <div key={c.id} className="flex items-start justify-between gap-3 px-3 py-2 rounded"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium" style={{ color: 'var(--evo-fg-1)' }}>{c.label}</span>
                  <Pill size="xs" variant="subtle" tone="muted">
                    {t(`messages.channel.${c.platform.replace('_business', '')}`, { defaultValue: c.platform })}
                  </Pill>
                  {!c.enabled && <Pill size="xs" variant="subtle" tone="muted">{t('settings.channels.off')}</Pill>}
                  {/* Leer y poder responder son cosas distintas y el cliente
                      tiene que poder distinguirlas sin preguntar. */}
                  {c.enabled && !c.verifiedAt && (
                    <Pill size="xs" variant="subtle" tone="amber">{t('settings.channels.readOnly')}</Pill>
                  )}
                  {!c.listo && <Pill size="xs" variant="subtle" tone="amber">{t('settings.channels.incomplete')}</Pill>}
                </div>
                <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                  {c.motivoNoListo ?? `ID ${c.externalId}`}
                </span>
              </div>
              {c.enabled && (
                <IconButton
                  icon={<Icon name="close" size={14} />}
                  ariaLabel={t('settings.channels.disable')}
                  tone="danger" variant="outline" size="md"
                  onClick={() => { void quitar(c); }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <Button variant="solid" tone="cyan" size="sm" loading={opciones.isPending} onClick={abrirElección}>
        {t('settings.channels.chooseFacebook')}
      </Button>

      {/* Conectar WhatsApp. Vivía SOLO en la inducción (2026-09-18): quien
          desconectaba el canal después se quedaba sin forma de volver a
          conectarlo —la lista de acá mostraba el número apagado y ningún botón
          para revivirlo, y el único formulario que existe estaba en una
          pantalla por la que ya se había pasado.

          Es el mismo componente, no una copia: si mañana cambia el flujo de
          conexión, cambia en los dos lados a la vez. */}
      <div style={{
        marginTop: 'var(--evo-space-5)', paddingTop: 'var(--evo-space-4)',
        borderTop: '1px solid var(--evo-stroke)',
      }}>
        <WhatsappConnect />
      </div>

      <Modal
        open={eligiendo !== null}
        onClose={() => setEligiendo(null)}
        title={t('settings.channels.chooseFacebook')}
        description={t('settings.channels.chooseHint')}
        size="md"
      >
        <div className="flex flex-col gap-2">
          {(eligiendo ?? []).length === 0 && (
            <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.channels.noPages')}</p>
          )}
          {(eligiendo ?? []).map(c => (
            <div key={c.externalId} className="flex items-center justify-between gap-3 px-3 py-2 rounded"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium" style={{ color: 'var(--evo-fg-1)' }}>{c.label}</span>
                <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>ID {c.externalId}</span>
              </div>
              {c.yaRegistrado ? (
                <Pill size="xs" variant="subtle" tone="muted">{t('settings.channels.already')}</Pill>
              ) : !c.canMessage ? (
                <Pill size="xs" variant="subtle" tone="amber">{t('settings.channels.noMessaging')}</Pill>
              ) : (
                <Button variant="outline" tone="cyan" size="sm" loading={registrar.isPending}
                  onClick={() => registrar.mutate(
                    { platform: 'facebook', externalId: c.externalId },
                    { onSuccess: () => setEligiendo(null), onError: errorDe },
                  )}>
                  {t('settings.channels.connect')}
                </Button>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}


// ── Contexto del negocio: el guion (Mensajería Etapa 2, 2026-08-20) ──────────
// Evo Brain arma un guion leyendo el sitio; acá se revisa, se corrige y se
// aprueba. Hasta que está aprobado no se usa para responder nada, y eso tiene
// que verse en pantalla — no deducirse.
function BusinessContextSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useBusinessContext();
  const saveUrl = useSaveWebsiteUrl();
  const generate = useGenerateContext();
  const approve = useApproveContext();
  const addFact = useAddFact();
  const updateFact = useUpdateFact();
  const deleteFact = useDeleteFact();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const [url, setUrl] = useState('');
  const [urlTocada, setUrlTocada] = useState(false);
  const [editando, setEditando] = useState<number | null>(null);
  const [textoEdit, setTextoEdit] = useState('');
  const [agregando, setAgregando] = useState(false);
  const [nueva, setNueva] = useState({ topic: 'other', statement: '' });
  const [resumen, setResumen] = useState<GenerateResult | null>(null);

  // El campo arranca con lo que ya está guardado (la inducción pudo haberlo
  // cargado) y deja de sincronizarse en cuanto lo tocan, para no pisar lo que
  // se está escribiendo cuando vuelve la query.
  useEffect(() => {
    if (!urlTocada && data?.websiteUrl != null) setUrl(data.websiteUrl);
  }, [data?.websiteUrl, urlTocada]);

  const errorDe = (err: unknown) => toast.error(err instanceof Error ? err.message : String(err));

  // Se edita el borrador si lo hay; si no, el guion vigente ("editable
  // después"). Nunca los dos a la vez: cuál se está tocando se dice arriba.
  const activo = data?.draft ?? data?.approved ?? null;
  const esBorrador = data?.draft != null;

  function generar() {
    setResumen(null);
    generate.mutate(url.trim() || undefined, {
      onSuccess: r => { setResumen(r); setUrlTocada(false); },
      onError: errorDe,
    });
  }

  async function aprobar(id: number) {
    const ok = await confirmAction({ title: t('settings.businessContext.approveConfirm'), tone: 'cyan' });
    if (!ok) return;
    approve.mutate(id, { onSuccess: () => setResumen(null), onError: errorDe });
  }

  async function borrar(factId: number) {
    const ok = await confirmAction({ title: t('settings.businessContext.deleteFactConfirm'), tone: 'danger' });
    if (!ok) return;
    deleteFact.mutate(factId, { onError: errorDe });
  }

  function guardarEdicion() {
    if (editando == null || !textoEdit.trim()) return;
    updateFact.mutate({ factId: editando, statement: textoEdit.trim() }, {
      onSuccess: () => setEditando(null), onError: errorDe,
    });
  }

  function guardarNueva() {
    if (!activo || !nueva.statement.trim()) return;
    addFact.mutate({ contextId: activo.id, topic: nueva.topic, statement: nueva.statement.trim() }, {
      onSuccess: () => { setNueva({ topic: 'other', statement: '' }); setAgregando(false); },
      onError: errorDe,
    });
  }

  return (
    <div className="mb-8">
      {confirmDialog}
      <SectionTitle>{t('settings.businessContext.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.businessContext.help')}
      </p>

      {/* Estado. Que Evo Brain NO esté respondiendo tiene que estar dicho, no
          deducido de la ausencia de un cartel verde. */}
      <div
        className="flex items-start gap-2 px-3 py-2 rounded mb-4"
        style={{
          background: 'var(--evo-bg-2)',
          border: `1px solid ${data?.approved ? 'var(--evo-stroke)' : 'var(--evo-warning)'}`,
        }}
      >
        <span style={{ color: data?.approved ? 'var(--evo-ok)' : 'var(--evo-warning)', display: 'inline-flex', flexShrink: 0, marginTop: '1px' }}>
          <Icon name={data?.approved ? 'ok' : 'alert'} size={14} />
        </span>
        <span className="text-xs" style={{ color: 'var(--evo-fg-2)', lineHeight: 1.5 }}>
          {data?.approved
            ? t('settings.businessContext.stateApproved', { date: fmtDate(data.approved.approvedAt ?? '') })
            : t('settings.businessContext.stateNotApproved')}
        </span>
      </div>

      {/* Sitio + generar */}
      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <Input
            value={url}
            onChange={v => { setUrl(v); setUrlTocada(true); }}
            label={t('settings.businessContext.urlLabel')}
            placeholder="minegocio.com"
            helperText={t('settings.businessContext.urlHelp')}
            fullWidth
          />
        </div>
        <Button
          variant="outline" tone="muted" size="sm"
          disabled={!url.trim() || saveUrl.isPending}
          loading={saveUrl.isPending}
          onClick={() => saveUrl.mutate(url.trim(), { onSuccess: () => setUrlTocada(false), onError: errorDe })}
        >
          {t('common.save')}
        </Button>
        <Button
          variant="solid" tone="cyan" size="sm"
          loading={generate.isPending}
          disabled={!url.trim() || generate.isPending}
          onClick={generar}
        >
          {t('settings.businessContext.generate')}
        </Button>
      </div>

      {/* Leer un sitio entero y pasarlo por Claude tarda. Sin esto la pantalla
          parece colgada y se aprieta Generar dos veces. */}
      {generate.isPending && <LoadingState size="sm" label={t('settings.businessContext.generating')} />}

      {/* Qué se leyó y qué no. Un guion armado con 1 de 6 páginas no es el
          mismo guion, y quien aprueba merece saberlo antes de aprobar. */}
      {resumen && (
        <div className="px-3 py-2 rounded mb-4" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--evo-fg-2)' }}>
            {t('settings.businessContext.summary', {
              accepted: resumen.context.facts.length,
              proposed: resumen.proposed,
              pages: resumen.scrape.read.length,
            })}
          </p>
          {resumen.rejected > 0 && (
            <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {t('settings.businessContext.summaryRejected', { count: resumen.rejected })}
            </p>
          )}
          {resumen.scrape.failed.map(f => (
            <p key={f.url} className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {f.url} — {f.reason}
            </p>
          ))}
        </div>
      )}

      {isLoading ? (
        <LoadingState size="sm" />
      ) : !activo ? (
        <EmptyState
          size="sm"
          title={t('settings.businessContext.emptyTitle')}
          description={t('settings.businessContext.emptyHint')}
        />
      ) : (
        <>
          <p className="text-sm font-medium mb-2" style={{ color: 'var(--evo-fg-2)' }}>
            {esBorrador ? t('settings.businessContext.draftTitle') : t('settings.businessContext.approvedTitle')}
          </p>

          <div className="space-y-1.5 mb-3">
            {activo.facts.map(f => (
              <div
                key={f.id}
                className="flex items-start justify-between gap-3 px-3 py-2 rounded"
                style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
              >
                {editando === f.id ? (
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <Textarea value={textoEdit} onChange={setTextoEdit} rows={3} maxLength={600} fullWidth />
                    <div className="flex items-center gap-2">
                      <Button variant="solid" tone="cyan" size="sm" loading={updateFact.isPending}
                        disabled={!textoEdit.trim()} onClick={guardarEdicion}>
                        {t('common.save')}
                      </Button>
                      <Button variant="ghost" tone="muted" size="sm" onClick={() => setEditando(null)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill size="xs" variant="subtle" tone="muted">
                          {t(`settings.businessContext.topic.${f.topic}`, { defaultValue: f.topic })}
                        </Pill>
                        {/* Sin fuente lo escribió una persona. Decirlo es la
                            diferencia entre "lo dice el sitio" y "lo dijiste
                            vos" — que es justo lo que se audita. */}
                        {f.origin === 'manual' && (
                          <Pill size="xs" variant="subtle" tone="amber">
                            {t('settings.businessContext.originManual')}
                          </Pill>
                        )}
                        {f.origin === 'edited' && (
                          <Pill size="xs" variant="subtle" tone="muted">
                            {t('settings.businessContext.originEdited')}
                          </Pill>
                        )}
                      </div>
                      <span className="text-sm" style={{ color: 'var(--evo-fg-1)', lineHeight: 1.5 }}>
                        {f.statement}
                      </span>
                      {f.sourceUrl ? (
                        <a
                          href={f.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs"
                          style={{ color: 'var(--evo-cyan)', textDecoration: 'none', wordBreak: 'break-all' }}
                          title={f.sourceQuote ?? undefined}
                        >
                          {f.sourceUrl}
                        </a>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                          {t('settings.businessContext.noSource')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <IconButton
                        icon={<Icon name="pencil" size={14} />}
                        ariaLabel={t('common.edit')}
                        tone="muted" variant="outline" size="md"
                        onClick={() => { setEditando(f.id); setTextoEdit(f.statement); }}
                      />
                      <IconButton
                        icon={<Icon name="close" size={14} />}
                        ariaLabel={t('common.delete')}
                        tone="danger" variant="outline" size="md"
                        onClick={() => { void borrar(f.id); }}
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {agregando ? (
            <div className="flex flex-col gap-2 px-3 py-2 rounded mb-3"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
              <Dropdown<string>
                value={nueva.topic}
                onChange={v => setNueva(n => ({ ...n, topic: v }))}
                options={['offering', 'pricing', 'hours', 'location', 'terms', 'other'].map(v => ({
                  value: v, label: t(`settings.businessContext.topic.${v}`),
                }))}
              />
              <Textarea
                value={nueva.statement}
                onChange={v => setNueva(n => ({ ...n, statement: v }))}
                rows={3}
                maxLength={600}
                placeholder={t('settings.businessContext.newFactPlaceholder')}
                helperText={t('settings.businessContext.newFactHelp')}
                fullWidth
              />
              <div className="flex items-center gap-2">
                <Button variant="solid" tone="cyan" size="sm" loading={addFact.isPending}
                  disabled={!nueva.statement.trim()} onClick={guardarNueva}>
                  {t('common.save')}
                </Button>
                <Button variant="ghost" tone="muted" size="sm" onClick={() => setAgregando(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="outline" tone="muted" size="sm" onClick={() => setAgregando(true)}>
                {t('settings.businessContext.addFact')}
              </Button>
              {esBorrador && (
                <Button
                  variant="solid" tone="cyan" size="sm"
                  loading={approve.isPending}
                  disabled={activo.facts.length === 0 || approve.isPending}
                  onClick={() => { void aprobar(activo.id); }}
                >
                  {t('settings.businessContext.approve')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section: Billing (company profile + accounts) ───────────────────────────────

// export: reusado tal cual (sin duplicar) por OnboardingPage.tsx — paso
// "datos de facturación" de la inducción (2026-08-05).
export function CompanyProfileForm() {
  const { t } = useTranslation();
  const { data: company, isLoading } = useCompany();
  const updateCompany = useUpdateCompany();
  const [form, setForm] = useState({
    name: '', legalName: '', address: '', taxId: '', email: '', phone: '',
    estimatePrefix: '', estimatePad: '3', invoicePrefix: '', invoicePad: '3',
    paymentTerms: '', legalFooter: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!company) return;
    setForm({
      name: company.name ?? '',
      legalName: company.legalName ?? '',
      address: company.address ?? '',
      taxId: company.taxId ?? '',
      email: company.email ?? '',
      phone: company.phone ?? '',
      estimatePrefix: company.estimatePrefix,
      estimatePad: String(company.estimatePad),
      invoicePrefix: company.invoicePrefix,
      invoicePad: String(company.invoicePad),
      paymentTerms: company.paymentTerms ?? '',
      legalFooter: company.legalFooter ?? '',
    });
  }, [company]);

  function setField(key: keyof typeof form, val: string) {
    setForm(f => ({ ...f, [key]: val }));
    setSaved(false);
  }

  function handleSave() {
    updateCompany.mutate(
      {
        name: form.name,
        legalName: form.legalName || null,
        address: form.address || null,
        taxId: form.taxId || null,
        email: form.email || null,
        phone: form.phone || null,
        estimatePrefix: form.estimatePrefix,
        estimatePad: Math.max(1, Math.trunc(Number(form.estimatePad) || 3)),
        invoicePrefix: form.invoicePrefix,
        invoicePad: Math.max(1, Math.trunc(Number(form.invoicePad) || 3)),
        paymentTerms: form.paymentTerms || null,
        legalFooter: form.legalFooter || null,
      },
      { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } },
    );
  }

  function previewFolio(prefix: string, pad: string, nextSeq: number | undefined) {
    const p = Math.max(1, Math.trunc(Number(pad) || 3));
    return prefix + String(nextSeq ?? 1).padStart(p, '0');
  }

  if (isLoading) return <LoadingState size="sm" />;

  return (
    <div>
      <SectionTitle>{t('billing.company.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.company.hint')}</p>

      <Field label={t('billing.company.name')}>
        <Input value={form.name} onChange={v => setField('name', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.legalName')}>
        <Input value={form.legalName} onChange={v => setField('legalName', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.address')}>
        <Textarea value={form.address} onChange={v => setField('address', v)} rows={2} fullWidth />
      </Field>
      <Field label={t('billing.company.taxId')}>
        <Input value={form.taxId} onChange={v => setField('taxId', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.email')}>
        <Input type="email" value={form.email} onChange={v => setField('email', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.phone')}>
        <Input value={form.phone} onChange={v => setField('phone', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.paymentTerms')} hint={t('billing.company.paymentTermsHint')}>
        <Textarea value={form.paymentTerms} onChange={v => setField('paymentTerms', v)} rows={2} fullWidth />
      </Field>
      <Field label={t('billing.company.legalFooter')} hint={t('billing.company.legalFooterHint')}>
        <Textarea value={form.legalFooter} onChange={v => setField('legalFooter', v)} rows={2} fullWidth />
      </Field>

      <SectionTitle>{t('billing.numbering.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.numbering.hint')}</p>

      <Field label={t('billing.numbering.estimatePrefix')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input value={form.estimatePrefix} onChange={v => setField('estimatePrefix', v)} size="sm" fullWidth /></div>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input type="number" value={form.estimatePad} onChange={v => setField('estimatePad', v)} size="sm" fullWidth /></div>
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('billing.numbering.nextPreview', { folio: previewFolio(form.estimatePrefix, form.estimatePad, company?.estimateNextSeq) })}
          </span>
        </div>
      </Field>

      <Field label={t('billing.numbering.invoicePrefix')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input value={form.invoicePrefix} onChange={v => setField('invoicePrefix', v)} size="sm" fullWidth /></div>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input type="number" value={form.invoicePad} onChange={v => setField('invoicePad', v)} size="sm" fullWidth /></div>
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('billing.numbering.nextPreview', { folio: previewFolio(form.invoicePrefix, form.invoicePad, company?.invoiceNextSeq) })}
          </span>
        </div>
      </Field>

      <div style={{ marginTop: 'var(--evo-space-4)' }}>
        <Button size="sm" tone="cyan" variant={saved ? 'outline' : 'solid'} loading={updateCompany.isPending} onClick={handleSave}>
          {updateCompany.isPending ? t('settings.savingBtn') : saved ? t('settings.savedBtn') : t('settings.saveBtn')}
        </Button>
      </div>
    </div>
  );
}

function AccountsList() {
  const { t } = useTranslation();
  const { data: company } = useCompany();
  const { data: accountsData, isLoading } = useAccounts(true);
  const accounts = accountsData?.accounts ?? [];
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', currency: '' });

  async function handleDelete(id: number) {
    const ok = await confirmAction({ title: t('billing.accounts.deleteConfirm'), confirmLabel: t('common.delete') });
    if (!ok) return;
    deleteAccount.mutate(id);
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!company) return;
    createAccount.mutate(
      { companyId: company.id, name: form.name, currency: form.currency },
      { onSuccess: () => { setForm({ name: '', currency: '' }); setShowForm(false); } },
    );
  }

  return (
    <div>
      <SectionTitle>{t('billing.accounts.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.accounts.hint')}</p>

      {isLoading && <LoadingState size="sm" />}

      {accounts.length === 0 && !isLoading && (
        <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.accounts.empty')}</p>
      )}

      <div className="space-y-2 mb-4">
        {accounts.map(acc => (
          <div
            key={acc.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            <div className="flex items-center gap-3">
              <span className="text-sm" style={{ color: 'var(--evo-fg-1)' }}>{acc.name}</span>
              <Pill tone="cyan" variant="outline" size="sm">{acc.currency.toUpperCase()}</Pill>
              <Pill tone={acc.isActive ? 'success' : 'muted'} variant="subtle" size="sm">
                {acc.isActive ? t('billing.accounts.active') : t('billing.accounts.inactive')}
              </Pill>
            </div>
            {acc.hasDocuments ? (
              <Button
                variant="outline"
                tone={acc.isActive ? 'muted' : 'cyan'}
                size="sm"
                loading={updateAccount.isPending}
                onClick={() => updateAccount.mutate({ id: acc.id, isActive: !acc.isActive })}
              >
                {acc.isActive ? t('billing.accounts.retire') : t('billing.accounts.reactivate')}
              </Button>
            ) : (
              <Button
                variant="outline"
                tone="danger"
                size="sm"
                loading={deleteAccount.isPending}
                onClick={() => void handleDelete(acc.id)}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
        ))}
      </div>

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3 p-4 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('billing.accounts.name')}
            </label>
            <Input
              value={form.name}
              onChange={v => setForm(f => ({ ...f, name: v }))}
              placeholder={t('billing.accounts.namePlaceholder')}
              required
              size="sm"
              fullWidth
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('billing.accounts.currency')}
            </label>
            <Input
              value={form.currency}
              onChange={v => setForm(f => ({ ...f, currency: v.toLowerCase() }))}
              placeholder={t('billing.accounts.currencyPlaceholder')}
              required
              size="sm"
              fullWidth
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createAccount.isPending}>
              {t('billing.accounts.addAccountBtn')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setShowForm(false)}>
              {t('billing.accounts.cancelBtn')}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowForm(true)} disabled={!company}>
          {t('billing.accounts.addAccount')}
        </Button>
      )}
      {confirmDialog}
    </div>
  );
}

function BillingSection() {
  const { t } = useTranslation();
  const { data: ajustes } = useAllSettings();
  const guardarAjuste = useUpdateSetting();
  return (
    <div className="space-y-8">
      {/* Idioma por defecto de cotizaciones y facturas. Cada documento puede
          tener el suyo; esto es lo que hereda cuando no lo tiene. */}
      <Field label={t('settings.billing.documentLanguage')} hint={t('settings.billing.documentLanguageHint')}>
        <Dropdown<string>
          value={ajustes?.['billing.document_language'] ?? 'es'}
          options={[
            { value: 'es', label: t('settings.generalSection.langEs') },
            { value: 'en', label: t('settings.generalSection.langEn') },
          ]}
          onChange={v => guardarAjuste.mutate({ key: 'billing.document_language', value: v })}
          size="sm"
        />
      </Field>
      <CompanyProfileForm />
      <AccountsList />
      <BillingItemsList />
    </div>
  );
}

/**
 * Catálogo de conceptos con precio.
 *
 * Vive dentro de Facturación y no en una pantalla del menú: se toca cada tanto,
 * cuando cambia un precio, no todos los días. Elegir un concepto al armar una
 * cotización COPIA descripción y precio; editarlos o borrarlos acá no toca
 * ningún documento ya emitido.
 */
function BillingItemsList() {
  const { t } = useTranslation();
  const { data, isLoading } = useBillingItems();
  const crear = useCreateBillingItem();
  const actualizar = useUpdateBillingItem();
  const borrar = useDeleteBillingItem();
  const [descripcion, setDescripcion] = useState('');
  const [precio, setPrecio] = useState('');

  const items = data?.items ?? [];
  const puedeAgregar = descripcion.trim().length > 0 && !crear.isPending;

  function agregar() {
    if (!puedeAgregar) return;
    crear.mutate(
      { description: descripcion.trim(), unitPrice: Math.max(0, Math.round((Number(precio) || 0) * 100)) },
      { onSuccess: () => { setDescripcion(''); setPrecio(''); } },
    );
  }

  return (
    <div className="space-y-3">
      <SectionTitle>{t('billing.estimates.itemsTitle')}</SectionTitle>
      <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', lineHeight: 1.6 }}>
        {t('billing.estimates.itemsHelp')}
      </p>

      {isLoading ? <LoadingState size="sm" /> : items.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-4)' }}>
          {t('billing.estimates.itemsEmpty')}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div
              key={item.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px var(--evo-icon-btn-sm)',
                gap: 'var(--evo-space-2)',
                alignItems: 'center',
              }}
            >
              <Input
                value={item.description}
                onChange={v => actualizar.mutate({ id: item.id, description: v })}
                size="sm"
                fullWidth
              />
              <Input
                type="number"
                value={(item.unitPrice / 100).toFixed(2)}
                onChange={v => actualizar.mutate({ id: item.id, unitPrice: Math.max(0, Math.round((Number(v) || 0) * 100)) })}
                size="sm"
                fullWidth
              />
              <IconButton
                icon={<Icon name="trash" size={13} />}
                ariaLabel={t('common.delete')}
                variant="ghost"
                tone="danger"
                size="sm"
                onClick={() => borrar.mutate(item.id)}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px auto', gap: 'var(--evo-space-2)', alignItems: 'end' }}>
        <Input
          label={t('billing.estimates.itemDescription')}
          value={descripcion}
          onChange={setDescripcion}
          size="sm"
          fullWidth
        />
        <Input
          label={t('billing.estimates.itemPrice')}
          type="number"
          value={precio}
          onChange={setPrecio}
          size="sm"
          fullWidth
        />
        <Button variant="outline" tone="cyan" size="sm" disabled={!puedeAgregar} loading={crear.isPending} onClick={agregar}>
          {t('billing.estimates.itemsAdd')}
        </Button>
      </div>
    </div>
  );
}

// Ventana para considerar "conectado" — no hay heartbeat dedicado, se infiere
// de la última llamada real registrada en audit_log (routes/external.ts).
// 15 min es generoso frente a cualquier polling razonable de OpenClaw sin
// marcar "desconectado" por un rato sin actividad normal.
const OPENCLAW_RECENT_MS = 15 * 60 * 1000;

function OpenClawSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useOpenClawStatus();
  const issueToken = useIssueOpenClawToken();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const toast = useToast();
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const isConnected = data?.lastSeenAt != null && (Date.now() - parseServerDate(data.lastSeenAt).getTime()) < OPENCLAW_RECENT_MS;

  async function handleIssueToken() {
    const ok = await confirmAction({
      title: data?.tokenIssued ? t('settings.openclaw.regenerate_confirm') : t('settings.openclaw.generate_confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    issueToken.mutate(undefined, { onSuccess: r => setRevealedToken(r.token) });
  }

  function handleCopy(text: string) {
    void navigator.clipboard.writeText(text);
    toast.success(t('settings.openclaw.copied'));
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.openclaw.title')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.openclaw.description')}</p>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Pill size="sm" variant="subtle" tone={isConnected ? 'success' : 'muted'}>
              {isConnected ? t('settings.openclaw.connected') : t('settings.openclaw.notConnected')}
            </Pill>
            {data.lastSeenAt && (
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('settings.openclaw.lastSeen', { date: fmtDate(data.lastSeenAt) })}
              </span>
            )}
          </div>

          <div className="space-y-3 mb-5">
            <Field label={t('settings.openclaw.endpoint_label')}>
              <div className="flex items-center gap-2">
                <Input value={data.endpoint} onChange={() => {}} size="sm" fullWidth disabled />
                <Button variant="outline" tone="muted" size="sm" onClick={() => handleCopy(data.endpoint)}>
                  {t('settings.openclaw.copy')}
                </Button>
              </div>
            </Field>

            <Field label={t('settings.openclaw.token_label')}>
              {revealedToken ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Input value={revealedToken} onChange={() => {}} size="sm" fullWidth disabled />
                    <Button variant="outline" tone="cyan" size="sm" onClick={() => handleCopy(revealedToken)}>
                      {t('settings.openclaw.copy')}
                    </Button>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>{t('settings.openclaw.token_shown_once')}</span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  tone={data.tokenIssued ? 'muted' : 'cyan'}
                  size="sm"
                  loading={issueToken.isPending}
                  onClick={() => { void handleIssueToken(); }}
                >
                  {data.tokenIssued ? t('settings.openclaw.regenerate_btn') : t('settings.openclaw.generate_btn')}
                </Button>
              )}
            </Field>
          </div>

          <DetailSection title={t('settings.openclaw.setup_title')}>
            <ol className="text-sm space-y-2 list-decimal pl-5" style={{ color: 'var(--evo-fg-2)' }}>
              <li>{t('settings.openclaw.setup_step1')}</li>
              <li>{t('settings.openclaw.setup_step2')}</li>
              <li>{t('settings.openclaw.setup_step3')}</li>
              <li>{t('settings.openclaw.setup_step4')}</li>
            </ol>
          </DetailSection>
        </>
      )}
    </div>
  );
}

function UsersSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useUsers();
  const createUser = useCreateUser();
  const updateRole = useUpdateUserRole();
  const setActive = useSetUserActive();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'member' as 'owner' | 'member' });
  const [formError, setFormError] = useState<string | null>(null);
  const users = data?.users ?? [];

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!newUser.email.trim() || newUser.password.length < 8) {
      setFormError(t('settings.users.validation', 'Email y contraseña (mín. 8 caracteres) requeridos.'));
      return;
    }
    createUser.mutate(
      { email: newUser.email.trim(), password: newUser.password, displayName: newUser.displayName.trim() || undefined, role: newUser.role },
      {
        onSuccess: () => { setNewUser({ email: '', password: '', displayName: '', role: 'member' }); setShowNew(false); },
        onError: (err: unknown) => setFormError(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  async function handleRoleChange(userId: number, role: 'owner' | 'member') {
    updateRole.mutate({ id: userId, role }, {
      onError: (err: unknown) => alert(err instanceof Error ? err.message : String(err)),
    });
  }

  async function handleToggleActive(userId: number, active: boolean) {
    if (!active) {
      const ok = await confirmAction({ title: t('settings.users.deactivate_confirm', '¿Desactivar este usuario? Deja de poder iniciar sesión.'), tone: 'danger' });
      if (!ok) return;
    }
    setActive.mutate({ id: userId, active }, {
      onError: (err: unknown) => alert(err instanceof Error ? err.message : String(err)),
    });
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.users.title', 'Usuarios')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.users.description', 'owner: acceso total, incluida esta pantalla y la suscripción. member: trabaja y ve la facturación a sus clientes, sin acceso a credenciales, integraciones ni la suscripción de la cuenta.')}
      </p>

      {isLoading && <LoadingState />}

      {!isLoading && (
        <div className="space-y-1.5 mb-4">
          {users.map(u => (
            <div
              key={u.id}
              className="flex items-center justify-between px-3 py-2 rounded"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', opacity: u.active ? 1 : 0.5 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{u.displayName || u.email}</span>
                <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{u.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <RadioGroup
                  value={u.role}
                  options={[
                    { value: 'member', label: t('settings.users.member', 'member') },
                    { value: 'owner', label: t('settings.users.owner', 'owner') },
                  ]}
                  onChange={v => { void handleRoleChange(u.id, v as 'owner' | 'member'); }}
                />
                <Toggle
                  checked={u.active}
                  onChange={v => { void handleToggleActive(u.id, v); }}
                  size="sm"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!showNew && (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNew(true)}>
          {t('settings.users.invite_btn', 'Crear usuario')}
        </Button>
      )}

      {showNew && (
        <form onSubmit={handleCreate} className="flex flex-col gap-2 p-3 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <Input value={newUser.email} onChange={v => setNewUser(p => ({ ...p, email: v }))} placeholder={t('settings.users.email_placeholder', 'Email')} size="sm" fullWidth />
          <Input value={newUser.password} onChange={v => setNewUser(p => ({ ...p, password: v }))} type="password" placeholder={t('settings.users.password_placeholder', 'Contraseña inicial (mín. 8 caracteres)')} size="sm" fullWidth />
          <Input value={newUser.displayName} onChange={v => setNewUser(p => ({ ...p, displayName: v }))} placeholder={t('settings.users.name_placeholder', 'Nombre (opcional)')} size="sm" fullWidth />
          <RadioGroup
            value={newUser.role}
            options={[
              { value: 'member', label: t('settings.users.member', 'member') },
              { value: 'owner', label: t('settings.users.owner', 'owner') },
            ]}
            onChange={v => setNewUser(p => ({ ...p, role: v as 'owner' | 'member' }))}
          />
          {formError && <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>{formError}</span>}
          <div className="flex gap-2">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createUser.isPending}>
              {t('settings.users.create_btn', 'Crear')}
            </Button>
            <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => { setShowNew(false); setFormError(null); }}>
              {t('settings.categoriesAndTags.cancel_btn')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Section: Consumo (Fase 4 — Settings → Consumo) ───────────────────────────

const SERVICE_LABEL_KEYS = new Set([
  'claude', 'groq', 'elevenlabs', 'google_tts', 'composio', 'google_places', 'pagespeed',
]);

function ApiUsageSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiUsage();
  // El tope de gasto lo decide EvoHub (auditoría 2026-08-18): la ruta pasó a
  // requirePlatformAdmin. Sin este gate, el cliente veía un campo editable que
  // le devolvía 403 al guardar — un control muerto, que es peor que no tenerlo.
  const { isPlatformAdmin } = useSession();
  const setLimit = useSetApiUsageLimit();
  const [limitValue, setLimitValue] = useState('');
  const [limitSaved, setLimitSaved] = useState(true);

  useEffect(() => {
    if (data && limitSaved) setLimitValue(data.limitUsd != null ? String(data.limitUsd) : '');
  }, [data, limitSaved]);

  function saveLimit() {
    const trimmed = limitValue.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) return;
    setLimit.mutate(parsed, { onSuccess: () => setLimitSaved(true) });
  }

  const pctOfLimit = data?.limitUsd ? Math.min(100, (data.monthSpendUsd / data.limitUsd) * 100) : 0;
  const limitColor = pctOfLimit >= 100 ? 'var(--evo-danger)' : pctOfLimit >= 80 ? 'var(--evo-warning)' : 'var(--evo-cyan)';

  return (
    <div>
      <SectionTitle>{t('settings.apiUsage.title', 'Consumo de APIs')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.apiUsage.description', 'Consumo de APIs externas pagas este mes, por servicio.')}
      </p>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          {data.billingExempt && (
            <div className="mb-4">
              <Pill size="sm" variant="subtle" tone="muted">
                {t('settings.apiUsage.exempt', 'Cuenta exenta — nunca se corta ni se cobra')}
              </Pill>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <Field label={t('settings.apiUsage.monthSpend', 'Gasto real este mes')}>
              <span className="text-sm font-mono">${data.monthSpendUsd.toFixed(2)}</span>
            </Field>
            <Field label={t('settings.apiUsage.estimatedCharge', 'Estimado a cobrar (doble del real)')}>
              <span className="text-sm font-mono">${data.estimatedChargeUsd.toFixed(2)}</span>
            </Field>
          </div>

          <div className="overflow-auto rounded mb-6" style={{ border: '1px solid var(--evo-stroke)' }}>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.service', 'Servicio')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.quantity', 'Volumen')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.cost', 'Costo')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.byService.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.apiUsage.noUsage', 'Sin consumo este mes')}</td></tr>
                )}
                {data.byService.map(row => (
                  <tr key={row.service} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
                    <td className="px-3 py-2 font-medium">
                      {SERVICE_LABEL_KEYS.has(row.service) ? t(`settings.apiUsage.services.${row.service}`, row.service) : row.service}
                    </td>
                    <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--evo-fg-3)' }}>{row.quantity.toLocaleString('es')}</td>
                    <td className="px-3 py-2 text-right font-mono">${row.costUsd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Field
            label={t('settings.apiUsage.limitLabel', 'Límite mensual')}
            hint={isPlatformAdmin
              ? t('settings.apiUsage.limitHint', 'Vacío = sin límite. Al superarlo se corta el consumo de APIs pagas sin afectar el resto de EvoHub.')
              : t('settings.apiUsage.limitHintClient', 'Al superarlo se corta el consumo de APIs pagas sin afectar el resto de EvoHub. Lo define EvoHub.')}
          >
            {isPlatformAdmin ? (
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>$</span>
                <div style={{ width: '96px', flexShrink: 0 }}>
                  <Input
                    type="number"
                    min={0}
                    step={10}
                    value={limitValue}
                    onChange={v => { setLimitValue(v); setLimitSaved(false); }}
                    placeholder={t('settings.apiUsage.limitPlaceholder', 'sin límite')}
                    size="sm"
                    fullWidth
                  />
                </div>
                <Button size="sm" tone="cyan" variant={limitSaved ? 'outline' : 'solid'} loading={setLimit.isPending} onClick={saveLimit}>
                  {setLimit.isPending ? t('settings.savingBtn') : limitSaved ? t('settings.savedBtn') : t('settings.saveBtn')}
                </Button>
              </div>
            ) : (
              <span className="text-sm" style={{ color: data.limitUsd != null ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)' }}>
                {data.limitUsd != null ? `$${data.limitUsd}` : t('settings.apiUsage.limitPlaceholder', 'sin límite')}
              </span>
            )}
          </Field>

          {data.limitUsd != null && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--evo-bg-2)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pctOfLimit}%`, background: limitColor }} />
                </div>
                <span className="text-sm font-mono w-28 text-right" style={{ color: limitColor }}>
                  ${data.monthSpendUsd.toFixed(2)} / ${data.limitUsd.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section: Suscripción (Fase 3 — Settings → Suscripción) ───────────────────

const SUBSCRIPTION_STATUS_TONE: Record<SubscriptionSummary['status'], 'success' | 'warn' | 'danger'> = {
  active: 'success', grace_period: 'warn', suspended: 'danger',
};

function fmtMoney(amountCents: number | null, currency: string | null): string {
  if (amountCents == null || !currency) return '—';
  return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

function SubscriptionSection() {
  const { t } = useTranslation();
  const { isPlatformAdmin } = useSession();
  const { data, isLoading } = useSubscription();
  const availablePlans = useAvailablePlans().data?.plans ?? [];
  const checkout = useCreateSubscriptionCheckout();
  const portal = useCreateBillingPortal();
  const toast = useToast();
  function goToCheckout(planId?: string) {
    checkout.mutate(planId, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  function goToPortal() {
    portal.mutate(undefined, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  const graceDaysLeft = data ? daysUntil(data.gracePeriodEndsAt) : null;

  return (
    <div>
      <SectionTitle>{t('settings.subscription.title', 'Suscripción')}</SectionTitle>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          {/* Prueba: mismo componente que la banda de la app, en bloque. */}
          <div style={{ marginBottom: 'var(--evo-space-4)' }}><TrialNotice variant="block" /></div>
          {data.billingExempt ? (
            <Pill size="sm" variant="subtle" tone="muted">
              {t('settings.subscription.exempt', 'Cuenta exenta — no paga suscripción')}
            </Pill>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Pill size="sm" variant="subtle" tone={SUBSCRIPTION_STATUS_TONE[data.status]}>
                  {t(`settings.subscription.status.${data.status}`, data.status)}
                </Pill>
                {data.status === 'grace_period' && graceDaysLeft != null && (
                  <span className="text-xs" style={{ color: 'var(--evo-warning)' }}>
                    {t('settings.subscription.graceDaysLeft', `Pago pendiente — ${graceDaysLeft} día(s) antes de suspender`, { count: graceDaysLeft })}
                  </span>
                )}
                {data.status === 'suspended' && (
                  <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>
                    {t('settings.subscription.suspendedNotice', 'Cuenta en solo lectura — regulariza el pago para volver a operar')}
                  </span>
                )}
              </div>

              <div className="space-y-3 mb-5">
                {/* Solo lectura desde el 2026-08-18: el precio de una cuenta
                    lo decide EvoHub, no el cliente. Se sigue mostrando —
                    esconderlo dejaría a alguien sin saber qué plan tiene. */}
                <Field label={t('settings.subscription.priceLabel', 'Plan')} hint={t('settings.subscription.priceHint', 'El plan de esta cuenta lo define EvoHub.')}>
                  <span className="text-sm flex items-center gap-2" style={{ color: data.priceId ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)' }}>
                    {/* El NOMBRE del plan, no el price_id: "price_1U0r5h..." no
                        le dice nada a nadie. El id queda para el admin. */}
                    {data.planName ?? data.priceId ?? t('settings.subscription.noPlan', 'Sin plan asignado')}
                    {/* El control de escritura vive en el panel de plataforma.
                        Sin este enlace, un admin busca el campo acá y no lo
                        encuentra — pasó el 2026-08-19. */}
                    {isPlatformAdmin && (
                      <Link to="/admin/planes" style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)' }}>
                        {t('settings.subscription.managePlans', 'Administrar planes')}
                      </Link>
                    )}
                  </span>
                </Field>

                {!data.hasCustomer && (
                  <>
                    {/* El precio ANTES de apretar el botón. Sin esto el cliente
                        se entera de cuánto le van a cobrar recién en la página
                        de Stripe — y eso genera una llamada. */}
                    <Field label={t('settings.subscription.price', 'Precio')}>
                      <span className="text-sm">
                        {data.priceAmount != null
                          ? `${fmtMoney(data.priceAmount, data.priceCurrency)} / ${t(`settings.subscription.interval.${data.priceInterval ?? 'month'}`, 'mes')}`
                          : '—'}
                      </span>
                    </Field>
                    {/* Sin plan no hay botón, hay explicación. Un botón gris
                        que no responde al clic no le dice a nadie qué hacer —
                        es el bug que reportó Eduardo el 2026-08-18. */}
                    {data.priceId ? (
                      <Button variant="solid" tone="cyan" size="sm" loading={checkout.isPending} onClick={() => goToCheckout()}>
                        {t('settings.subscription.subscribeBtn', 'Suscribirme')}
                      </Button>
                    ) : availablePlans.length > 0 ? (
                      // Mismo selector que el paso 7: quien llega acá después de
                      // que venció la prueba tiene que poder elegir y pagar sin
                      // volver a la inducción.
                      <div className="flex flex-col" style={{ gap: 'var(--evo-space-3)' }}>
                        {availablePlans.map(plan => (
                          <div key={plan.id} className="flex items-center gap-3">
                            <span className="text-sm" style={{ flex: 1, minWidth: 0, color: 'var(--evo-fg-1)' }}>{plan.name}</span>
                            <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>
                              {plan.amount != null ? `${fmtMoney(plan.amount, plan.currency)}${plan.interval ? ` / ${t(`settings.subscription.interval.${plan.interval}`, plan.interval)}` : ''}` : '—'}
                            </span>
                            <Button variant="solid" tone="cyan" size="sm" loading={checkout.isPending} onClick={() => goToCheckout(plan.id)}>
                              {t('settings.subscription.chooseBtn', 'Elegir')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                        {t('settings.subscription.noPlanNotice', 'Todavía no tienes un plan asignado. Escríbenos y lo dejamos listo para que puedas activar la suscripción.')}
                      </span>
                    )}
                  </>
                )}

                {/* Consumo del mes — lo ve el cliente ANTES de que llegue la
                    factura. La factura dice qué se cobró; esto dice por qué, y
                    antes. Se muestra aunque el cobro todavía no esté activo:
                    medir sin cobrar deja que el cliente vea el número antes de
                    que empiece a costarle. */}
                {data.usage && (
                  <Field label={t('settings.subscription.usageTitle', 'Consumo de API este mes')}>
                    <div className="flex flex-col" style={{ gap: 'var(--evo-space-1)' }}>
                      <span className="text-sm">
                        {fmtMoney(data.usage.estimatedChargeCents, 'usd')}
                        <span style={{ color: 'var(--evo-fg-3)' }}>
                          {' '}{data.usage.billingActive
                            ? t('settings.subscription.usageEstimated', '(estimado, se suma a la próxima factura)')
                            : t('settings.subscription.usageNotBilled', '(informativo — todavía no se cobra)')}
                        </span>
                      </span>
                      {data.usage.coveredUsd > 0 && (
                        <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                          {t('settings.subscription.usageCovered', 'Incluido sin cargo: {{amount}}', { amount: fmtMoney(Math.round(data.usage.coveredUsd * 100), 'usd') })}
                        </span>
                      )}
                      {data.usage.capCents != null && (
                        <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                          {t('settings.subscription.usageCap', 'Tope garantizado: nunca más de {{amount}} por mes', { amount: fmtMoney(data.usage.capCents, 'usd') })}
                        </span>
                      )}
                      {data.usage.byService.map(r => (
                        <span key={r.label} className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                          {r.label}: {new Intl.NumberFormat('es').format(Math.round(r.grossQuantity))}
                          {r.tierQuantity != null && ` · ${new Intl.NumberFormat('es').format(Math.round(r.freeQuantity))} ${t('settings.subscription.usageFree', 'sin cargo')}`}
                        </span>
                      ))}
                    </div>
                  </Field>
                )}

                {data.hasCustomer && (
                  <>
                    <Field label={t('settings.subscription.nextCharge', 'Próximo cobro')}>
                      <span className="text-sm">
                        {data.nextChargeAt ? fmtDate(data.nextChargeAt) : '—'} — {fmtMoney(data.nextChargeAmount, data.nextChargeCurrency)}
                      </span>
                    </Field>
                    {data.paymentMethod && (
                      <Field label={t('settings.subscription.paymentMethod', 'Método de pago')}>
                        <span className="text-sm capitalize">{data.paymentMethod.brand} •••• {data.paymentMethod.last4}</span>
                      </Field>
                    )}
                    <Button variant="outline" tone="muted" size="sm" loading={portal.isPending} onClick={goToPortal}>
                      {t('settings.subscription.managePaymentBtn', 'Gestionar método de pago')}
                    </Button>
                  </>
                )}
              </div>

              {data.invoices.length > 0 && (
                <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.date', 'Fecha')}</th>
                        <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.amount', 'Monto')}</th>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.status', 'Estado')}</th>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map(inv => (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
                          <td className="px-3 py-2">{inv.date ? fmtDate(inv.date) : '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(inv.amountPaid, inv.currency)}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{inv.status ?? '—'}</td>
                          <td className="px-3 py-2">
                            {inv.hostedInvoiceUrl && (
                              <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--evo-cyan)' }}>
                                {t('settings.subscription.viewInvoice', 'Ver')}
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────────────

// Fase 2 (2026-08-04): ownerOnly acá es solo UI — oculta la tab para no
// mostrar un botón que va a 403. El chequeo real es requireOwner() en el
// backend (routes/users.ts, routes/settings.ts, routes/email.ts) — esto
// nunca es la única barrera.
// Las 16 pestañas viejas se agrupan en SEIS. Las reglas de visibilidad
// (ownerOnly / platformOnly / módulo apagado) se conservan POR TARJETA, no
// por pestaña: una pestaña nunca se oculta completa — si todas sus tarjetas
// están vedadas, muestra un vacío que explica por qué. El 403 real sigue en
// el backend; esto es solo la capa de UI.
// La lista vive en lib/settingsTabs.ts, no acá: los mensajes que mandan a una
// pestaña necesitan la MISMA lista para armar el enlace, y tenerla dos veces es
// justo cómo cuatro avisos terminaron apuntando a pestañas que ya no existían.
const TABS6 = SETTINGS_TABS.map(id => ({ id }));

type TabId = SettingsTabId;

/* Tarjeta de Configuración: caja del design system alrededor de una sección
 * existente (que conserva su propio título). */
function SettingsCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="evo-detail-card"
      style={{
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r-md)',
        padding: 'var(--evo-card-pad)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

/* Fila de la pestaña: dos tarjetas → duo a altura pareja; una → ancho
 * completo. Los nulos (tarjetas vedadas por rol/módulo) se filtran acá. */
function SettingsRow({ cards }: { cards: Array<React.ReactNode | false | null> }) {
  const visibles = cards.filter(Boolean);
  if (visibles.length === 0) return null;
  if (visibles.length === 1) {
    return <SettingsCard>{visibles[0]}</SettingsCard>;
  }
  return (
    <div className="evo-duo-grid">
      {visibles.map((c, i) => <SettingsCard key={i}>{c}</SettingsCard>)}
    </div>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const { role, isPlatformAdmin, modules } = useSession();
  useEffect(() => {
    setOverride({ title: t('settings.pageTitle'), heroIcon: 'settings', heroIconColor: 'var(--evo-fg-3)' });
    return () => setOverride({});
  }, [setOverride, t]);
  // La pestaña vive en la URL: los enlaces entre pestañas (ej. el aviso de
  // guion en Automatización) aterrizan donde apuntan, y volver con el
  // navegador cae en la pestaña que estabas mirando.
  const [tab, setTab] = useTabParam(TABS6.map(x => x.id), 'account');

  // Reglas por TARJETA (heredadas de las 16 pestañas viejas). El 403 real
  // vive en el backend — esto solo decide qué se pinta.
  const owner = role === 'owner';
  const can = {
    owner,
    platform: isPlatformAdmin,
    email: modules['email' as AccountModuleKey],
    leads: modules['leads' as AccountModuleKey],
    messaging: modules['messaging' as AccountModuleKey],
    billing: modules['billing' as AccountModuleKey],
  };

  // Filas por pestaña, según la tabla de mapeo 16→6. `false` = tarjeta vedada
  // (se filtra en SettingsRow); una fila sin tarjetas no se pinta.
  const ROWS: Record<TabId, Array<Array<React.ReactNode | false>>> = {
    account: [
      [<SessionCard key="s" />, <PreferencesCard key="p" />, <AvisosCard key="a" />],
      // La ayuda también se entra desde acá, como complemento del «?» del
      // borde: alguien que va a buscar ayuda deliberadamente pasa por Ajustes
      // (Eduardo, 2026-09-12). Lo que no puede es vivir SOLO acá — la ayuda
      // sirve cuando alguien está trabado, y nadie se va a Ajustes entonces.
      [<AyudaCard key="ay" />],
      [can.owner && <UsersSection key="u" />],
      [can.owner && <ApiUsageSection key="au" />, can.owner && <SubscriptionSection key="sub" />],
    ],
    brand: [
      [<IdentityCard key="i" />, <PublicContactCard key="pc" />],
      [can.messaging && <BusinessContextSection key="bc" />],
    ],
    channels: [
      [can.owner && can.email && <ImapSection key="imap" />],
      [can.messaging && <MessagingChannelsSection key="mc" />, can.email && <RulesSection key="r" />],
      [can.messaging && <SavedRepliesSection key="sr" />],
      // Las plantillas van DESPUÉS de las respuestas guardadas a propósito: la
      // mayoría de lo que un negocio escribe cabe en una respuesta guardada, y
      // encontrar primero la que cuesta dinero invita a usarla de más.
      [can.messaging && <PlantillasWhatsapp key="wt" />],
    ],
    // Automatización tiene su propio layout (handoff/automatizacion.html):
    // rótulos de grupo + tarjetas propias, no filas genéricas.
    automation: [],
    data: [
      // El modo de Stripe va acá, al lado de Facturación: es donde alguien lo
      // busca. Gateado por platform admin — decide si se cobra dinero real.
      [can.platform && <StripeModeCard key="sm" />],
      [<CategoriesTagsSection key="ct" />, can.billing && <BillingSection key="b" />],
      [<CleanupSection key="cl" />],
    ],
    system: [
      [can.owner && <OwnApiKeysSection key="ok" />],
      [can.platform && <ImageGenerationSection key="ig" />, can.owner && can.platform && <OpenClawSection key="oc" />],
      [can.platform && <ApiSection key="api" />],
      [<AuditSection key="a" />],
    ],
  };

  const rows = tab === 'automation'
    ? [<AutomationTab key="auto" can={{ leads: !!can.leads, email: !!can.email, messaging: !!can.messaging }} />]
    : ROWS[tab].map((cards, i) => <SettingsRow key={i} cards={cards} />);
  const hasVisible = tab === 'automation' || ROWS[tab].some(cards => cards.some(Boolean));

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <SettingsClaudePanel />
      {/* Seis pestañas alineadas a la izquierda — pantalla de sección. */}
      <div
        className="shrink-0"
        style={{
          padding: '0 var(--evo-page-pad-x)',
          height: 'var(--evo-subnav-h)', borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex', alignItems: 'flex-end',
          overflowX: 'auto', overflowY: 'hidden',
        }}
      >
        <Tabs<TabId>
          tabs={TABS6.map(tabItem => ({
            value: tabItem.id,
            label: t(`settings.tabs6.${tabItem.id}`),
          }))}
          value={tab}
          onChange={v => setTab(v)}
          size="sm"
          noBorder
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div style={{ padding: 'var(--evo-space-6) var(--evo-page-pad-x)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
          {hasVisible ? rows : <EmptyState title={t('settings.emptyTab')} />}
        </div>
      </div>
    </div>
  );
}
